import { useEffect as useDrawerBaselineEffect, useEffectEvent as useDrawerBaselineEvent } from "react";
import { useDirtySnapshot } from "../../../../../../shared/frontend/forms/useDirtySnapshot";
import { useDrawerDraft, useDrawerWorkspaceClose } from "../../../../../../shared/frontend/components/hooks/useDrawerDraftScope";
import { formSnapshot } from "../../../../../../shared/frontend/forms/formSnapshot";
import {spacing as designSpacing,spacing} from "../../app/design-system";
import { ArrowLeftOutlined, DeleteOutlined, DollarOutlined, FilterOutlined, PauseCircleOutlined, PlusOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../shared/frontend/list/listColumn";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Flex, Form, Grid, Input, InputNumber, Segmented, Select, Space, Switch, Tabs, Tag, theme, Tooltip, Typography, ActionMenu, type ActionMenuItem, Checkbox } from "../../../../../../shared/frontend/ui";
import { DatePicker } from "../../../../../../shared/frontend/components/ControlledPickers";
import dayjs from "dayjs";
import React from "react";
import { useLocation,useNavigate,useParams,useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/auth/useAuth";
import { useFeatureAccess } from "../../app/auth/useFeatureAccess";
import { Permissions } from "../../app/constants/permissions";
import { PRICE_ADJUSTMENT_TYPE_FIXED,PRICE_ADJUSTMENT_TYPE_PERCENTAGE,PRICE_ADJUSTMENT_TYPE_SET_PRICE } from "../../app/constants/priceAdjustmentType";
import { SERVICE_PRODUCT_TYPE_MEMBERSHIP } from "../../app/constants/serviceProductType";
import { TIME_UNIT_DAYS,TIME_UNIT_MONTHS,TIME_UNIT_WEEKS,TIME_UNIT_YEARS,type TimeUnitValue } from "../../app/constants/timeUnit";
import { accessScopeToLabel } from "../../app/i18n/accessScopeTexts";
import { useI18n } from "../../app/i18n/useI18n";
import { reportsLabelKeyForPath } from "../reports/reportsCatalog";
import { emitPersonsChanged } from "../../app/persons/personsEvents";
import { useCheckInDeviceLocation } from "../../app/checkin/useCheckInDeviceLocation";
import { useAccessMediumAssignmentReaders } from "../../features/accessDevices/useAccessMediumAssignmentReaders";
import { useGlobalStudioContext } from "../../app/studios/useGlobalStudioContext";
import { useDateInputFormat } from "../../app/units/DateInputFormatContext";
import {
  decimalSeparatorOf,
  formatDateOnly,
  moneyPlaceholderOf
} from "../../app/units/format";
import { loadUnits } from "../../app/units/unitsStorage";
import { billingCycleOptions } from "../../app/utils/billingCycle";
import { cycleUnitSelectOptions,formatCycleUnitLabel } from "../../app/utils/cycleUnitI18n";
import { paymentMethodDefinitionSelectOptions } from "../../app/utils/paymentMethod";
import { PAYMENT_METHOD_SEPA } from "../../app/utils/paymentMethodCodes";
import { useProTableSortPersistence } from "../../app/utils/proTableSortPersistence";
import { useProTableColumnEditor } from "../../app/utils/useProTableColumnEditor";
import {
  buildDefaultOptionGroupSelection,
  type OptionGroupSelectionState
} from "../../app/utils/contractOptionGroups";
import { FormDrawer } from "../../components/FormDrawer";
import { FormSectionHeading } from "../../components/FormSectionHeading";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { useSendDocumentReviewWorkspace } from "../../components/communication/useSendDocumentReviewWorkspace";
import type { PaymentItem } from "../../components/PaymentDrawer";
import { SettingsListPage,type ProColumnsWithMeasure,type SettingsListActionRef } from "../../components/SettingsListPage";
import { CancelContractDrawer,RestPeriodDrawer } from "../../components/contracts";
import { RefundTransferDialogs, type RefundTransferTask } from "../finance/RefundTransferDialogs";
import { AccessMediumAssignModal } from "./personDetail/AccessMediumAssignModal";
import { PersonArticleSalesTab } from "./personDetail/PersonArticleSalesTab";
import { PersonChangeHistoryTab } from "./personDetail/PersonChangeHistoryTab";
import { PersonContractCreateDrawer } from "./personDetail/PersonContractCreateDrawer";
import { ContractAggregatorSection } from "./personDetail/ContractAggregatorSection";
import { usePersonAggregatorRequirements } from "../../app/integrations/usePersonAggregatorIdentities";
import { PersonContributionBulkPayDrawer } from "./personDetail/PersonContributionBulkPayDrawer";
import { PersonContributionEditDrawer } from "./personDetail/PersonContributionEditDrawer";
import { PersonDigitalFileTab } from "./personDetail/PersonDigitalFileTab";
import { reassignAccessMedium as reassignAccessMediumAction } from "./personDetail/personAccessActions";
import {
createPersonAccessMediaColumns,
PERSON_ACCESS_MEDIA_COLUMN_DEFINITIONS
} from "./personDetail/personAccessMediaTableUtils";
import {
createPersonContractColumns,
PERSON_CONTRACT_COLUMN_DEFINITIONS
} from "./personDetail/personContractColumns";
import { useSetPagePin } from "../../app/layout/LayoutPagePinContext";
import { buildPersonPin } from "../../app/pins/pinnedLinks";
import { lifecycleLabelKey } from "./lifecycle";
import { PersonPaymentEditDrawer } from "./personDetail/PersonPaymentEditDrawer";
import { PersonPaymentCancellationDrawer } from "./personDetail/PersonPaymentCancellationDrawer";
import { PaymentCancellationReminders, type PaymentCancellationReminderRequest } from "../../components/PaymentCancellationReminders";
import { PersonPaymentRecordDrawers } from "./personDetail/PersonPaymentRecordDrawers";
import { PersonPaymentsTab } from "./personDetail/PersonPaymentsTab";
import { canEditPersonPayment } from "./personDetail/personPaymentEditability";
import { PersonAccountStatementTab } from "./personDetail/PersonAccountStatementTab";
import { PersonOpenItemsTab } from "./personDetail/PersonOpenItemsTab";
import { PersonPrivacyDrawer } from "./personDetail/PersonPrivacyDrawer";
import { PersonProfileTab } from "./personDetail/PersonProfileTab";
import { PersonPromotionsTab } from "./personDetail/PersonPromotionsTab";
import { PersonOverviewLayoutActions, PersonOverviewTab } from "./personDetail/PersonOverviewTab";
import { usePersonOverviewLayout } from "./personDetail/overview/usePersonOverviewLayout";
import { PersonServiceEditDrawer } from "./personDetail/PersonServiceEditDrawer";
import { PersonWalletEditDrawer } from "./personDetail/PersonWalletEditDrawer";
import { PersonWalletsTab } from "./personDetail/PersonWalletsTab";
import type {
  ContractCreateFormValues,
  ContractPriceAdjustmentRow,
  ContractRow,
  ContractServiceDraft,
  ContractWalletDraft,
  ContributionCalendarRow,
  PaymentRow,
  PersonFormValues,
  PersonOpenItemRow,
  PersonPaymentFormValues,
  RestPeriodRow
} from "./personDetail/personDetailTypes";
import {
  backLabelKeyForPath,
  computeInstallmentsPreviewWithRounding,
  normalizePersonDetailTabParam,
  readPersonBackTarget,
  rememberPersonBackTarget,
  timeUnitLabel as timeUnitLabelBase
} from "./personDetail/personDetailUtils";
import {
getPersonDetailTabState,
groupPersonDetailTabs,
type PersonDetailTabVisibility
} from "./personDetail/personDetailTabUtils";
import { useAccessMediumScan } from "./personDetail/useAccessMediumScan";
import { accessMediaReadPermissions, accessMediaWritePermissions } from "../../app/constants/accessMediaPermissions";
import { useMemberAppRequestDrawer } from "./personDetail/useMemberAppRequestDrawer";
import { usePersonCommunicationActions } from "./personDetail/usePersonCommunicationActions";
import { usePersonContractListController } from "./personDetail/usePersonContractListController";
import { usePersonDetailDisplayPreferences } from "./personDetail/usePersonDetailDisplayPreferences";
import { usePersonDetailLookups } from "./personDetail/usePersonDetailLookups";
import { usePersonDetailRecord } from "./personDetail/usePersonDetailRecord";
import { useFinanceOperations } from "../../app/operations/useFinanceOperation";
import { usePersonDetailRealtime } from "./personDetail/usePersonDetailRealtime";
import { invalidatePersonFinanceData,PERSON_DETAIL_PAYMENTS_GC_TIME_MS,PERSON_DETAIL_PAYMENTS_STALE_TIME_MS,personDetailQueryKeys } from "./personDetail/personDetailQueryKeys";
import { usePersonTimelineFilters } from "./personDetail/usePersonTimelineFilters";
import { usePersonContractEditController } from "./personDetail/usePersonContractEditController";
import { usePersonContributionsController } from "./personDetail/usePersonContributionsController";
import { checkInEligibilityColor,usePersonCheckInEligibility } from "./personDetail/usePersonCheckInEligibility";
import { usePersonPhotoController } from "./personDetail/usePersonPhotoController";
import { ImageCaptureField } from "../../../../../../shared/frontend/components/ImageCaptureField";
import { usePersonPriceAdjustmentController } from "./personDetail/usePersonPriceAdjustmentController";
import { usePersonRestPeriodsController } from "./personDetail/usePersonRestPeriodsController";
import { usePersonWalletDrawer } from "./personDetail/usePersonWalletDrawer";
import { lifecycleTag } from "./utils";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { ACCESS_SCOPE_ANY_STUDIO } from "../../app/constants/accessScope";
import { ISO_DATE_LENGTH } from "../../app/utils/isoDate";
import { DEFAULT_MAX_INSTALLMENT_COUNT } from "../settings/contractTemplates/contractTemplateConstants";
import { PRIVACY_STATUS } from "./personDetail/personPrivacyValues";

const TAB_KEY_CONTRACTS = "contracts";
const TAB_KEY_OPEN_ITEMS = "openitems";
const TAB_KEY_WALLETS = "wallets";
const TAB_KEY_OVERVIEW = "overview";
const TAB_KEY_PROFILE = "profile";
const PERSON_ACTION = {
  cancellation: "cancellation", privacy: "privacy", contractBundle: "contractBundle",
  serviceAdd: "serviceAdd", walletCreate: "walletCreate", sendEmail: "sendEmail", sendLetter: "sendLetter", delete: "delete"
} as const;
/** Stand 2 = Vorlage nach Platzhalter-Position: Pauschal-Platzhalter stehen hinter Hauptleistung und Einzelleistungen. */
const SERVICE_ORDER_PLACEHOLDER = 2;
const START_PRESET_NEXT_DAY = 2;
const START_PRESET_NEXT_MONTH_START = 3;
const START_PRESET_DAY_OF_MONTH = 4;
const MAX_DAY_OF_MONTH = 31;
const MIN_PERCENT_ADJUSTMENT = -100;

type PersonDetailPageProps = {
  personId?: string;
  /** Wenn gesetzt (z. B. im Drawer): Back-Button schließt/verlässt statt Router-Navigation. */
  onBack?: () => void;
  /** Optional: LabelKey für Zurück-Button, falls state.from nicht verfügbar (z. B. Drawer). */
  backLabelKey?: string;
  /** Wenn aus Aufgabenliste geöffnet: ID der Member-App-Anfrage (Kündigung/Ruhezeit), damit der passende Drawer geöffnet wird. */
  initialMemberAppRequestId?: string;
};

/** Stabile leere Auswahl, damit die Ableitung keine neue Referenz je Render erzeugt. */
const EMPTY_OPTION_GROUP_SELECTION: OptionGroupSelectionState = {};

export function PersonDetailPage({ personId: personIdProp, onBack: onBackProp, backLabelKey: backLabelKeyProp, initialMemberAppRequestId }: PersonDetailPageProps = {}) {
  const onBack = useDrawerWorkspaceClose(onBackProp);
  const auth = useAuth();
  const { hasAccess: hasMemberPromotions } = useFeatureAccess("memberPromotions");
  const queryClient = useQueryClient();
  const loc = useLocation();
  const nav = useNavigate();
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const { text } = useI18n();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const dateInputFormat = useDateInputFormat();
  const { studioIdsQueryParam: openItemsStudioIdsParam, allowedStudios, studiosLoaded, studiosLoadFailed, refetchStudios } = useGlobalStudioContext();
  const { locationStudioId } = useCheckInDeviceLocation(allowedStudios, studiosLoaded);
  const assignmentStudioId = !studiosLoadFailed && studiosLoaded && allowedStudios.some(studio => studio.id === locationStudioId) ? locationStudioId : null;

  // Berechtigungen: Nur APIs aufrufen / Daten laden, die der Nutzer sehen darf – vermeidet 403-Toasts bei eingeschränkten Rollen.
  const canReadContracts = auth.hasAnyPermission(Permissions.ContractsRead, Permissions.ContractsClose, Permissions.ContractsUpdate, Permissions.ContractsDelete, Permissions.ContractsCancel);
  const canReadFinance = auth.hasAnyPermission(Permissions.PaymentsRead, Permissions.PaymentsWrite, Permissions.OpenItemsRead, Permissions.OpenItemsWrite);
  const canReadPayments = auth.hasAnyPermission(Permissions.PaymentsRead, Permissions.PaymentsWrite);
  const canReadOpenItems = auth.hasAnyPermission(Permissions.OpenItemsRead, Permissions.OpenItemsWrite);
  const canReadArticleSales = auth.hasAnyPermission(Permissions.ArticleSalesRead, Permissions.ArticleSalesWrite);
  const canReadCheckins = auth.hasAnyPermission(Permissions.CheckinViewRead, Permissions.CheckinUse);
  const canLoadPaymentMethodDefs = canReadFinance || auth.hasAnyPermission(Permissions.ContractsClose);
  const canLoadSepaGroups = auth.hasAnyPermission(Permissions.SepaRead, Permissions.SepaWrite);
  const canLoadRestTemplates = auth.hasAnyPermission(Permissions.RestPeriodsRead, Permissions.RestPeriodsWrite);
  const canLoadPaymentPlans = auth.hasAnyPermission(Permissions.PaymentPlansRead, Permissions.PaymentPlansWrite);
  const canLoadCancellationModels = auth.hasAnyPermission(Permissions.CancellationModelsRead, Permissions.CancellationModelsWrite);
  const canLoadPriceAdjustments = auth.hasAnyPermission(Permissions.PriceAdjustmentsRead, Permissions.PriceAdjustmentsWrite);
  const canLoadContractTemplateBlocks = auth.hasAnyPermission(Permissions.ContractsRead, Permissions.ContractsUpdate, Permissions.ContractsClose);
  const canWritePerson = auth.hasAnyPermission(Permissions.PersonsUpdate);
  const canDeletePerson = auth.hasAnyPermission(Permissions.PersonsDelete);
  const canReadDocuments = auth.hasAnyPermission(Permissions.PersonsDocumentsRead);
  const canWriteDocuments = auth.hasAnyPermission(Permissions.PersonsDocumentsWrite);
  const canReadChangeHistory = auth.hasAnyPermission(Permissions.PersonsChangeHistoryRead);
  const canBlockCheckIn = auth.hasAnyPermission(Permissions.PersonsBlockCheckIn);
  const canManagePrivacy = auth.hasAnyPermission(Permissions.PersonsPrivacyManage);
  const canViewPrivacy = canManagePrivacy || auth.hasAnyPermission(Permissions.PersonsRead, Permissions.PersonsUpdate);
  const canCloseContracts = auth.hasAnyPermission(Permissions.ContractsClose);
  const canWriteContracts = auth.hasAnyPermission(Permissions.ContractsUpdate);
  const canCancelContracts = auth.hasAnyPermission(Permissions.ContractsCancel);
  const canWriteFinance = auth.hasAnyPermission(Permissions.PaymentsWrite);
  const canCancelPayments = auth.hasAnyPermission(Permissions.PaymentsCancel);
  const canAgreeInstallment = auth.hasAnyPermission(Permissions.OpenItemsAgreeInstallment, Permissions.OpenItemsWrite, Permissions.PaymentsWrite);
  const canReadAccessMedia = auth.hasAnyPermission(...accessMediaReadPermissions);
  const canWriteAccessMedia = auth.hasAnyPermission(...accessMediaWritePermissions);
  const canReadRestPeriods = auth.hasAnyPermission(Permissions.RestPeriodsRead, Permissions.RestPeriodsWrite);
  const canWriteRestPeriods = auth.hasAnyPermission(Permissions.RestPeriodsWrite);
  const canReadPriceAdjustments = auth.hasAnyPermission(Permissions.PriceAdjustmentsRead, Permissions.PriceAdjustmentsWrite);
  const canWritePriceAdjustments = auth.hasAnyPermission(Permissions.PriceAdjustmentsWrite);
  const canCustomizePriceAdjustments = auth.hasAnyPermission(Permissions.PriceAdjustmentsCustomize);
  const canReadMemberPromotions = hasMemberPromotions && auth.hasAnyPermission(Permissions.MemberPromotionsRead, Permissions.MemberPromotionsWrite, Permissions.MemberPromotionsExecute);
  const canWriteMemberPromotions = hasMemberPromotions && auth.hasAnyPermission(Permissions.MemberPromotionsWrite, Permissions.MemberPromotionsExecute);
  const canExecuteMemberPromotions = hasMemberPromotions && auth.hasAnyPermission(Permissions.MemberPromotionsExecute);
  const canSendCommunication = auth.hasAnyPermission(Permissions.CommunicationSend);
  // Contract packages use SettingsListPage, including sort persistence.
  // sortWallets entfernt: SettingsListPage übernimmt Sort-Persistence intern (storageKey="persons.detail.wallets").
  const sortContributions = useProTableSortPersistence(auth.info?.accountId, "persons.detail.contributions");

  const {
    showHistoricalContracts,
    setShowHistoricalContracts,
    showHistoricalWallets,
    setShowHistoricalWallets,
    showHistoricalArticleSales,
    setShowHistoricalArticleSales
  } = usePersonDetailDisplayPreferences(auth.info?.accountId);

  /** Wenn gesetzt (z. B. aus Drawer), wird diese ID verwendet; sonst useParams().id */
  const personId = personIdProp !== undefined ? String(personIdProp) : String(id || "");
  const tab = normalizePersonDetailTabParam(sp.get("tab"));
  const memberAppRequestIdParam = sp.get("memberAppRequestId") ?? initialMemberAppRequestId ?? undefined;
  const privacyRequestIdParam = sp.get("privacyRequestId") ?? undefined;

  // Back target from router state.
  const navigateBackState = loc.state as { from?: string } | undefined;
  const fromIncoming =
    typeof navigateBackState?.from === "string" && navigateBackState.from.trim() !== ""
      ? navigateBackState.from.trim()
      : "";
  // Bei Folgenavigation (z. B. Tab per setSearchParams) und nach einem Reload ist location.state weg;
  // dann greift die pro Person gemerkte Herkunft aus dem Storage (unten im Effect geschrieben).
  const backTarget = React.useMemo(
    () => fromIncoming || readPersonBackTarget(personId) || "/persons",
    [fromIncoming, personId]
  );
  const backLabelKeyFromLocation = backLabelKeyForPath(backTarget);
  const backReportLabelKey = reportsLabelKeyForPath(backTarget.split("?")[0]);
  const backLabel = backLabelKeyProp
    ? text(backLabelKeyProp)
    : backReportLabelKey
      ? text("Back to report: {0}").replace("{0}", text(backReportLabelKey))
      : text(backLabelKeyFromLocation ?? "Back");

  React.useEffect(() => {
    if (fromIncoming) rememberPersonBackTarget(personId, fromIncoming);
  }, [fromIncoming, personId]);

  const [form] = Form.useForm<PersonFormValues>();
  const handleBlockedPersonLoad = React.useCallback(
    (reason: "forbidden" | "missing") => {
      const forbidden = reason === "forbidden";
      // Drawer/MemoryRouter: kein personIdProp, aber onBack (z. B. Dashboard)
      if (onBack) {
        feedback.warning(
          text(forbidden ? "Person detail no access embedded" : "Person detail not found embedded")
        );
        onBack();
        return;
      }
      feedback.warning(
        text(forbidden ? "Person detail no access redirect to list" : "Person detail not found redirect to list")
      );
      nav(backTarget, { replace: true });
    },
    [backTarget, feedback, nav, onBack, text]
  );

  const { store: personOperationStore } = useFinanceOperations();
  const {
    person,
    setPerson,
    personDetailFetchError,
    personBankName,
    setPersonBankName,
    loading,
    activeCheckInAt,
    saving,
    dirty: profileDirty,
    savePerson: savePersonRecord,
    changeHomeStudio,
    refreshPerson,
    load
  } = usePersonDetailRecord({
    operationStore: personOperationStore,
    api: auth.api,
    personId,
    form,
    canReadCheckins,
    onBlockedLoad: handleBlockedPersonLoad
  });
  useDrawerDraft(profileDirty, Boolean(onBack), saving);

  /**
   * Pin der Personenakte an die Titelleiste melden (Name + Foto statt generischem Routentitel).
   * Nur für die echte Route — im eingebetteten Drawer bleibt der Pin der Hintergrundseite stehen.
   */
  useSetPagePin(
    person && personId && personIdProp === undefined
      ? buildPersonPin({
          personId,
          label: `${person.firstName || ""} ${person.lastName || ""}`.trim() || text("(no name)"),
          subtitleKey: lifecycleLabelKey(person.lifecycleStatus),
          hasPhoto: person.hasPhoto
        })
      : null
  );

  const hideProspectDetailSections = person?.hideProspectDetailSections === true;

  const {
    timelineDirectionFilter,
    setTimelineDirectionFilter,
    timelineTypeFilter,
    setTimelineTypeFilter
  } = usePersonTimelineFilters(auth.info?.accountId);
  

  const [contractOpen, setContractOpen] = React.useState(false);
  const [contractForm] = Form.useForm<ContractCreateFormValues>();
  const [contractCreateWatchedValues, setContractCreateWatchedValues] = React.useState<Partial<ContractCreateFormValues>>({});
  const syncContractCreateWatchedValues = React.useCallback(() => {
    setContractCreateWatchedValues({
      contractTemplateVariantId: contractForm.getFieldValue("contractTemplateVariantId"),
      paymentPlanInstallmentCount: contractForm.getFieldValue("paymentPlanInstallmentCount"),
      startDate: contractForm.getFieldValue("startDate"),
      trainingStartDate: contractForm.getFieldValue("trainingStartDate")
    });
  }, [contractForm]);
  const isContractsTabActive = tab === TAB_KEY_CONTRACTS;
  const aggregatorRequirements = usePersonAggregatorRequirements(personId, isContractsTabActive && canReadContracts);
  const isOpenItemsTabActive = tab === TAB_KEY_OPEN_ITEMS;
  const isWalletsTabActive = tab === TAB_KEY_WALLETS;
  const shouldLoadContractLookups = canLoadContractTemplateBlocks && (isContractsTabActive || contractOpen);
  // Zahlungsarten sind eine winzige Lookup-Liste und werden in fast jedem Tab/Drawer für die
  // Zahlart-Auswahl gebraucht (Zahlungen, Beiträge, offene Posten, Verträge). Daher immer laden,
  // sobald erlaubt – sonst zeigt die Auswahl mangels Definitionen nur die rohe Typ-Zahl.
  const shouldLoadPaymentMethodLookups = canLoadPaymentMethodDefs;
  const shouldLoadSepaLookups = canLoadSepaGroups && (isContractsTabActive || contractOpen);
  const shouldLoadRestLookups = canLoadRestTemplates && (isContractsTabActive || contractOpen);
  const shouldLoadPaymentPlans = canLoadPaymentPlans && (isContractsTabActive || isOpenItemsTabActive || contractOpen);
  const shouldLoadCancellationModels = canLoadCancellationModels && isContractsTabActive;
  const shouldLoadPriceAdjustments = canLoadPriceAdjustments && isContractsTabActive;
  const {
    contractTemplates,
    walletProducts,
    contractTemplateBlocks,
    contractOptionGroups,
    setContractTemplateBlocks,
    paymentPlans,
    cancellationModels,
    priceAdjustments,
    accounts,
    paymentMethodDefs,
    sepaGroups,
    restTemplates,
    loadContractTemplates,
    loadAccounts
  } = usePersonDetailLookups({
    api: auth.api,
    canLoadPaymentMethodDefs: shouldLoadPaymentMethodLookups,
    canLoadSepaGroups: shouldLoadSepaLookups,
    canLoadRestTemplates: shouldLoadRestLookups,
    canLoadPaymentPlans: shouldLoadPaymentPlans,
    canLoadContractTemplateBlocks: shouldLoadContractLookups || isWalletsTabActive,
    canLoadCancellationModels: shouldLoadCancellationModels,
    canLoadPriceAdjustments: shouldLoadPriceAdjustments
  });
  const [contractCancelOpen, setContractCancelOpen] = React.useState(false);
  const [contractCancelItem, setContractCancelItem] = React.useState<ContractRow | null>(null);
  const [cancelChoosablePackageIds, setCancelChoosablePackageIds] = React.useState<string[] | null>(null);
  /** Wenn gesetzt: Drawer wurde aus Aufgabenliste geöffnet; beim Speichern wird diese Anfrage als bestätigt markiert. */
  const [memberAppRequestIdForDrawer, setMemberAppRequestIdForDrawer] = React.useState<string | null>(null);
  /** Vorbelegung Kündigungsdatum aus App-Anfrage (wenn Drawer aus Aufgabe geöffnet). */
  const [cancelPrefillFromRequest, setCancelPrefillFromRequest] = React.useState<{ requestedEffectiveDate: string } | null>(null);
  const [closingAccountId, setClosingAccountId] = React.useState<string>("");
  const { token } = theme.useToken();
  /** Zutrittszustand: nur als Rahmenfarbe am Personenfoto; die Begründung steht im Zutrittsbereich der Übersicht. */
  const { data: checkInEligibility } = usePersonCheckInEligibility(personId, !!person);
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const useStackedToolbar = screens.sm === false;
  const [contractFilterDrawerOpen, setContractFilterDrawerOpen] = React.useState(false);
  const useCompactTabNavigation = screens.xl === false;
  const watchedVariantId = contractCreateWatchedValues.contractTemplateVariantId;
  const watchedInstallmentCount = contractCreateWatchedValues.paymentPlanInstallmentCount;
  const watchedContractStartDate = contractCreateWatchedValues.startDate;
  const watchedTrainingStartDate = contractCreateWatchedValues.trainingStartDate;

  const contractsRef = React.useRef<SettingsListActionRef>(null);
  const walletsRef = React.useRef<SettingsListActionRef>(null);
  const walletDrawer = usePersonWalletDrawer({
    api: auth.api,
    walletProducts,
    walletsRef,
    contractsRef,
    setPerson
  });

  const accessMediaRef = React.useRef<SettingsListActionRef>(null);
  const assignmentReaders = useAccessMediumAssignmentReaders(assignmentStudioId, canWriteAccessMedia);
  const {
    open: accessMediaOpen,
    setOpen: setAccessMediaOpen,
    saving: accessMediaSaving,
    setSaving: setAccessMediaSaving,
    scanHint: accessMediumScanHint,
    scanConflict: accessMediumScanConflict,
    close: closeAccessMediaDrawer,
    retry: retryAccessMediumScan
  } = useAccessMediumScan({
    person,
    personId,
    studioId: assignmentStudioId,
    readerDeviceHardwareIds: assignmentReaders.readerIds,
    canStart: !!assignmentStudioId && assignmentReaders.isSuccess && assignmentReaders.readers.length > 0,
    api: auth.api,
    message: feedback,
    text,
    accessMediaRef
  });
  const reassignAccessMedium = React.useCallback(async (mediumId: string) => {
    try {
      setAccessMediaSaving(true);
      await reassignAccessMediumAction({
        api: auth.api,
        personId,
        mediumId,
        message: feedback,
        text,
        onDone: () => {
          closeAccessMediaDrawer();
          accessMediaRef.current?.reload?.();
        }
      });
    } catch {
      // Central API toast shows error
    } finally {
      setAccessMediaSaving(false);
    }
  }, [auth.api, closeAccessMediaDrawer, feedback, personId, text]);
  // PaymentDrawer state für Payments-Tab
  const [paymentsPaymentDrawerOpen, setPaymentsPaymentDrawerOpen] = React.useState(false);
  const [paymentsPaymentDrawerItems] = React.useState<PaymentItem[]>([]);
  const [paymentsPaymentDrawerSelectedItems, setPaymentsPaymentDrawerSelectedItems] = React.useState<PaymentItem[]>([]);
  const [paymentsSelectedRowKeys, setPaymentsSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [paymentsSelectedRows, setPaymentsSelectedRows] = React.useState<PaymentRow[]>([]);
  const [paymentCancellationDrawerOpen, setPaymentCancellationDrawerOpen] = React.useState(false);
  const [paymentCancellationDrawerPayments, setPaymentCancellationDrawerPayments] = React.useState<PaymentRow[] | null>(null);
  const [paymentCancellationReminders, setPaymentCancellationReminders] = React.useState<PaymentCancellationReminderRequest | null>(null);
  // Offene Posten: gleiche Struktur wie Beiträge – State und PaymentDrawer auf Top-Level
  const [openItemsSelectedRowKeys, setOpenItemsSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [openItemsSelectedRows, setOpenItemsSelectedRows] = React.useState<PersonOpenItemRow[]>([]);
  const [openItemsPaymentDrawerOpen, setOpenItemsPaymentDrawerOpen] = React.useState(false);
  const [openItemsPaymentDrawerItems, setOpenItemsPaymentDrawerItems] = React.useState<PaymentItem[]>([]);
  const [openItemsPaymentDrawerSelectedItems, setOpenItemsPaymentDrawerSelectedItems] = React.useState<PaymentItem[]>([]);
  const paymentsRef = React.useRef<SettingsListActionRef>(null);
  const openItemsRef = React.useRef<SettingsListActionRef>(null);
  const filesRef = React.useRef<SettingsListActionRef>(null);
  const { openDocumentReview, documentReviewWorkspace } = useSendDocumentReviewWorkspace({
    onSent: () => void filesRef.current?.revalidateCurrentQuery(),
  });
  const contributionsRef = React.useRef<SettingsListActionRef>(null);
  usePersonDetailRealtime(personId, () => accessMediaRef.current?.reload?.());
  const reloadContributions = React.useCallback(() => void contributionsRef.current?.revalidateCurrentQuery(), [contributionsRef]);
  const contributionsController = usePersonContributionsController({
    api: auth.api,
    personId,
    accountId: auth.info?.accountId,
    reloadFinanceRows: reloadContributions,
    canWriteFinance,
    canAgreeInstallment,
    dateInputFormat,
    text,
    units,
    sortContributions
  });
  // Die vollständige Liste wird beim Öffnen der Akte benötigt und von Übersicht und
  // Zahlungsreiter über denselben Query-Key wiederverwendet.
  const paymentsQuery = useQuery({
    queryKey: personDetailQueryKeys.person.payments(personId),
    queryFn: () =>
      auth.api.get<PaymentRow[]>(`/api/v1/persons/${encodeURIComponent(personId)}/payments?groupTransactions=true`, {
        suppressGlobalErrorToast: true
      }),
    // Erst nach erfolgreichem Person-Laden: sonst 404-Kaskade bei ungültiger ID / Race mit URL.
    enabled: !!personId && !!person && String(person.id) === personId,
    retry: false,
    staleTime: PERSON_DETAIL_PAYMENTS_STALE_TIME_MS,
    gcTime: PERSON_DETAIL_PAYMENTS_GC_TIME_MS
  });
  const bookedContractIds = React.useMemo(() => {
    return new Set((paymentsQuery.data || []).flatMap((payment) => payment.allocations ?? [payment]).map((payment) => payment.contractPackageId).filter((id): id is string => !!id));
  }, [paymentsQuery.data]);

  React.useEffect(() => {
    if (contractOpen) {
      syncContractCreateWatchedValues();
    }
  }, [contractOpen, syncContractCreateWatchedValues]);

  const contractEditController = usePersonContractEditController({
    api: auth.api,
    personId,
    canWriteContracts,
    sepaGroups,
    bookedContractIds,
    message: feedback,
    modal,
    text
  });
  const {
    contractEdit,
    contractEditOpen,
    contractEditSaving,
    contractEditForm,
    contractEditHasDifferentPaymentMethods,
    contractEditIsLocked,
    setContractEditIsLocked,
    setContractEditPaymentMethod,
    contractEditSingleBlock,
    serviceEdit,
    serviceEditOpen,
    serviceEditIsLocked,
    setServiceEditIsLocked,
    serviceEditForm,
    openContractPackageEdit,
    tryOpenCombinedOrServiceEdit,
    onContractsTableRowDoubleClick,
    closeContractPackageEdit,
    saveContractPackageEdit,
    deleteContractPackageEdit,
    closeServiceEdit,
    markServiceEditSaved
  } = contractEditController;
  const contractEditFields = Form.useWatch(formSnapshot, { form: contractEditForm, preserve: true });
  const { dirty: contractEditDirty, markSaved: contractEditMarkSaved } = useDirtySnapshot({ fields: contractEditFields, extra: { contractEditIsLocked } });
  const contractEditInitializeDirty = useDrawerBaselineEvent(() => contractEditMarkSaved({ fields: formSnapshot(contractEditForm.getFieldsValue(true)), extra: { contractEditIsLocked } }));
  useDrawerBaselineEffect(() => { if (contractEditOpen) contractEditInitializeDirty(); }, [contractEditOpen]);


  const contractListController = usePersonContractListController({
    api: auth.api,
    personId,
    canCancelContracts,
    canReadUsers: auth.hasAnyPermission(Permissions.UsersRead),
    // Am Sammelkonto kein Vorschlag — dort bestimmt das Kürzel beim Abschließen den Abschlussbenutzer.
    currentAccountId: auth.info?.accountId && !auth.info.isCollectionUser ? String(auth.info.accountId) : null,
    canWriteContracts,
    contractForm,
    contractTemplateBlocks,
    setContractTemplateBlocks,
    showHistoricalContracts,
    text,
    message: feedback,
    modal,
    loadAccounts,
    loadContractTemplates,
    openContractPackageEdit,
    setClosingAccountId,
    setContractCancelItem,
    setContractCancelOpen,
    setContractOpen,
    tryOpenCombinedOrServiceEdit,
    onContractsChanged: () => contractsRef.current?.reload?.(),
    onContributionsChanged: () => void contributionsRef.current?.revalidateCurrentQuery(),
    onOpenItemsChanged: () => openItemsRef.current?.reload?.()
  });
  const {
    canCancelContractPackage,
    contracts,
    contractsActionSlots,
    isContractPackageRow,
    loadContractPackagesForOverview,
    loadContractRows,
    openContractBundle
  } = contractListController;

  const priceAdjustmentController = usePersonPriceAdjustmentController({
    api: auth.api,
    personId,
    text,
    units,
    priceAdjustments,
    canCustomizePriceAdjustments,
    message: feedback,
    modal,
    onContributionsChanged: () => void contributionsRef.current?.revalidateCurrentQuery(),
    onOpenItemsChanged: () => openItemsRef.current?.reload?.()
  });

  const paymentMethodNameByType = React.useMemo(() => {
    const map: Record<number, string> = {};
    for (const m of paymentMethodDefs || []) {
      map[m.paymentMethodType] = String(m.name || "").trim() || String(m.paymentMethodType);
    }
    return map;
  }, [paymentMethodDefs]);

  const paymentMethodSelectOptions = React.useMemo(
    () => paymentMethodDefinitionSelectOptions(paymentMethodDefs),
    [paymentMethodDefs]
  );

  const cycleUnitLabel = React.useCallback((u?: number | null) => formatCycleUnitLabel(u, text), [text]);
  const cycleUnitSelectOptionsForServiceEdit = React.useMemo(() => cycleUnitSelectOptions(text), [text]);

  const billingCycleLabel = React.useCallback(
    (value?: number | null, unit?: number | null) => {
      const u = unit == null ? null : Number(unit);
      const v = value == null ? null : Number(value);
      const unitStr = cycleUnitLabel(u);
      if (!unitStr) return "";
      if (v != null && v > 1) return text("Payment cycle: every {0} {1}").replace("{0}", String(v)).replace("{1}", unitStr);
      return unitStr;
    },
    [text, cycleUnitLabel]
  );

  const timeUnitLabel = React.useCallback((u?: number | null) => timeUnitLabelBase(u, text), [text]);
  const currencyCode = (units.currency || "").trim() || "EUR";
  const decimalSeparator = React.useMemo(() => decimalSeparatorOf(units), [units.locale, units.numberLocale]);
  const moneyPlaceholder = React.useMemo(() => moneyPlaceholderOf(units), [units.locale, units.numberLocale]);
  const invalidateFinanceQueries = React.useCallback(() => {
    invalidatePersonFinanceData(queryClient, personId);
  }, [personId, queryClient]);
  // AccessMedium wird jetzt per "Scan & Save" angelegt, kein manuelles Form mehr.

  const usageLimitUnitLabel = React.useCallback((u?: TimeUnitValue | null) => {
    if (u === TIME_UNIT_DAYS) return text("per day");
    if (u === TIME_UNIT_WEEKS) return text("per week");
    if (u === TIME_UNIT_MONTHS) return text("per month");
    if (u === TIME_UNIT_YEARS) return text("per year");
    return "";
  }, [text]);

  const accessScopeLabel = React.useCallback((s?: number | null) => accessScopeToLabel(s, text), [text]);

  const {
    communicationMode,
    communicationSaving,
    communicationForm,
    openCommunication,
    closeCommunication,
    sendCommunication
  } = usePersonCommunicationActions({
    api: auth.api,
    text,
    message: feedback,
    person,
    allowedStudios,
    setSearchParams: setSp,
    onDocumentsChanged: () => void filesRef.current?.revalidateCurrentQuery()
  });

  const restPeriodsController = usePersonRestPeriodsController({
    api: auth.api,
    personId,
    canWriteRestPeriods,
    text,
    units,
    message: feedback,
    modal,
    onMemberAppRequestClosed: () => setMemberAppRequestIdForDrawer(null)
  });

  useMemberAppRequestDrawer({
    api: auth.api,
    message: feedback,
    text,
    requestId: memberAppRequestIdParam,
    personId,
    person,
    waitsForPerson: Boolean(onBack),
    canCancelContracts,
    canWriteRestPeriods,
    setSearchParams: setSp,
    setMemberAppRequestIdForDrawer,
    setContractCancelItem,
    setCancelPrefillFromRequest,
    setContractCancelOpen,
    setRestEdit: restPeriodsController.setEdit,
    setRestPrefillFromRequest: restPeriodsController.setPrefill,
    setRestOpen: restPeriodsController.setOpen
  });

  const savePerson = React.useCallback(async () => {
    const saved = await savePersonRecord({
      bankName: personBankName,
      // Stammstudio nur änderbar mit Recht persons.changeHomeStudio (nicht mit Personen bearbeiten)
      canChangeHomeStudio: auth.hasAnyPermission(Permissions.PersonsChangeHomeStudio)
    });
    if (saved) {
      void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.detail(personId) });
      feedback.success(text("Saved."));
    }
  }, [auth, feedback, personBankName, personId, queryClient, savePersonRecord, text]);

  const personPhotoController = usePersonPhotoController({
    api: auth.api,
    person,
    personId,
    load,
    message: feedback,
    text
  });
  const [privacyDrawerOpen, setPrivacyDrawerOpen] = React.useState(false);
  const [refundTransfer, setRefundTransfer] = React.useState<RefundTransferTask | null>(null);
  const closeRefundTransfer = React.useCallback(() => setRefundTransfer(null), [setRefundTransfer]);
  React.useEffect(() => {
    if (privacyRequestIdParam) setPrivacyDrawerOpen(true);
  }, [privacyRequestIdParam]);

  // Anordnung der Übersicht: hier, weil ihr Zahnrad in der Reiterleiste der Akte steht.
  const overviewLayout = usePersonOverviewLayout();

  // Vorgangsleiste: die häufigsten Vorgänge direkt aus dem Aktenkopf. Sie öffnen dieselben Drawer wie die
  // Einstiege in den Tabs und aus den Aufgaben — kein eigener Fachpfad.
  const showContractQuickActions = person?.isMember === true && !hideProspectDetailSections;
  const openRestPeriodDrawer = React.useCallback(() => {
    restPeriodsController.setEdit(null);
    restPeriodsController.setPrefill(null);
    restPeriodsController.setOpen(true);
  }, [restPeriodsController]);
  const openRestPeriodQuickAction = React.useCallback(() => {
    // Der Ruhezeit-Drawer wählt das Vertragspaket selbst vor; danach steht die Liste der Ruhezeiten offen.
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "restPeriods");
      return next;
    });
    openRestPeriodDrawer();
  }, [openRestPeriodDrawer, setSp]);
  const openCancellationQuickAction = React.useCallback(async () => {
    const cancellable = (await loadContractPackagesForOverview()).filter(canCancelContractPackage);
    if (cancellable.length === 0) {
      feedback.info(text("No cancellable contract package."));
      return;
    }
    // Bei mehreren kündbaren Paketen wählt man im Drawer selbst; vorausgewählt ist keines.
    setCancelPrefillFromRequest(null);
    setCancelChoosablePackageIds(cancellable.length > 1 ? cancellable.map((item) => item.id) : null);
    setContractCancelItem(cancellable[0]);
    setContractCancelOpen(true);
  }, [canCancelContractPackage, feedback, loadContractPackagesForOverview, setCancelChoosablePackageIds, setContractCancelItem, setContractCancelOpen, text]);
  const openPaymentQuickAction = React.useCallback(() => {
    setPaymentsPaymentDrawerSelectedItems([]);
    setPaymentsPaymentDrawerOpen(true);
  }, [setPaymentsPaymentDrawerOpen, setPaymentsPaymentDrawerSelectedItems]);

  const actions = React.useMemo(() => {
    const items: Array<{ key: string; label: string; danger?: boolean } | { type: "divider" }> = [];
    if (showContractQuickActions && canCancelContracts) {
      items.push({ key: PERSON_ACTION.cancellation, label: text("Cancellation…") });
      items.push({ type: "divider" as const });
    }
    if (canViewPrivacy) {
      items.push({ key: PERSON_ACTION.privacy, label: text("Privacy") });
      items.push({ type: "divider" as const });
    }
    if (canCloseContracts) {
      items.push({ key: PERSON_ACTION.contractBundle, label: text("Add contract package…") });
      items.push({ type: "divider" as const });
    }
    if (canSendCommunication) {
      items.push({ key: PERSON_ACTION.sendLetter, label: text("Send letter…") });
      items.push({ key: PERSON_ACTION.sendEmail, label: text("Send email…") });
    }
    if (canDeletePerson) {
      if (items.length > 0) items.push({ type: "divider" as const });
      items.push({ key: PERSON_ACTION.delete, label: text("Delete person action"), danger: true });
    }
    return items;
  }, [canCancelContracts, canCloseContracts, canDeletePerson, canSendCommunication, canViewPrivacy, showContractQuickActions, text]);

  const onAction = async (key: string) => {
    if (!person) return;
    if (key === PERSON_ACTION.cancellation) {
      await openCancellationQuickAction();
      return;
    }
    if (key === PERSON_ACTION.privacy) {
      setPrivacyDrawerOpen(true);
      return;
    }
    if (key === PERSON_ACTION.contractBundle) {
      await openContractBundle(person);
    }

    if (key === PERSON_ACTION.serviceAdd || key === PERSON_ACTION.walletCreate) {
      feedback.info(
        text("Please use contract packages (manual adding is currently disabled).")
      );
      return;
    }

    if (key === PERSON_ACTION.sendEmail) {
      if (!person.email) {
        feedback.error(text("No email address on file."));
        return;
      }
      await openCommunication("email");
      return;
    }
    if (key === PERSON_ACTION.sendLetter) {
      await openCommunication("letter");
      return;
    }
    if (key === PERSON_ACTION.delete) {
      modal.confirm({
        title: text("Delete person"),
        content: text("Delete person confirmation"),
        okText: text("Delete"),
        okButtonProps: { danger: true },
        cancelText: text("Cancel"),
        icon: <DeleteOutlined />,
        onOk: async () => {
          try {
            await auth.api.del(`/api/v1/persons/${encodeURIComponent(personId)}`);
            emitPersonsChanged({ reason: "deleted", personId });
            feedback.success(text("Person deleted."));
            if (onBack) onBack();
            else nav(backTarget, { replace: true });
          } catch (error) {
            feedback.error(error instanceof Error ? error.message : text("Delete failed"));
          }
        }
      });
      return;
    }
  };

  const accessMediaAllColumnsMap = React.useMemo(
    () => createPersonAccessMediaColumns(text, units),
    [text, units]
  );

  // Use column editor hook for access media
  const accessMediaColumnEditor = useProTableColumnEditor(
    "persons-detail-access-media-columns",
    PERSON_ACCESS_MEDIA_COLUMN_DEFINITIONS,
    accessMediaAllColumnsMap,
    text
  );

  const contractsAllColumnsMap: Record<string, ListColumn<ContractRow>> = React.useMemo(
    () => createPersonContractColumns({
      text,
      units,
      paymentMethodNameByType,
      contractTemplateBlocks,
      colorError: token.colorError
    }),
    [text, units, paymentMethodNameByType, contractTemplateBlocks, token.colorError]
  );

  const [paymentEdit, setPaymentEdit] = React.useState<PaymentRow | null>(null);
  const [paymentEditOpen, setPaymentEditOpen] = React.useState(false);
  const [paymentEditForm] = Form.useForm<PersonPaymentFormValues>();

  const contractVariantOptions = React.useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [];
    contractTemplates.forEach((t) => {
      // Templates werden direkt verwendet, keine Varianten mehr
      opts.push({ value: t.id, label: t.name || "" });
    });
    return opts;
  }, [contractTemplates]);

  const contractVariantMetaById = React.useMemo(() => {
    const map = new Map<
      string,
      {
        price: number;
        billingCycle: number;
        allowOverridesOnCreate: boolean;
        label: string;
      }
    >();
    contractTemplates.forEach((t) => {
      // Templates werden direkt verwendet, keine Varianten mehr
      map.set(t.id, {
        price: 0, // Price wird jetzt pro Component verwaltet, nicht mehr pro Template
        billingCycle: 1, // BillingCycle wird jetzt pro Component verwaltet, nicht mehr pro Template
        allowOverridesOnCreate: t.allowOverridesOnCreate !== false,
        label: t.name || ""
      });
    });
    return map;
  }, [contractTemplates]);

  const [bundleServiceDraft, setBundleServiceDraft] = React.useState<
    ContractServiceDraft[]
  >([]);
  const [bundleWalletDraft, setBundleWalletDraft] = React.useState<
    ContractWalletDraft[]
  >([]);
  // Zubuchoptionen: Der Zustand hält nur, was der Bediener abweichend gewählt hat — je Paketvorlage.
  // Die wirksame Auswahl wird daraus abgeleitet; solange nichts angefasst wurde, gilt die Vorauswahl
  // der Vorlage. Das vermeidet einen Effekt, der bei jedem Vorlagenwechsel State nachzieht.
  const [optionGroupOverrides, setOptionGroupOverrides] = React.useState<Record<string, OptionGroupSelectionState>>({});

  const selectedVariant = React.useMemo(() => {
    const vid = String(watchedVariantId || "");
    if (!vid) return null;
    // Templates werden direkt verwendet, keine Varianten mehr
    const template = contractTemplates.find(t => String(t.id) === vid);
    if (template) {
      return template;
    }
    return null;
  }, [contractTemplates, watchedVariantId]);

  React.useEffect(() => {
    const v = selectedVariant;
    if (!v) {
      setBundleServiceDraft((prev) => (prev.length === 0 ? prev : []));
      setBundleWalletDraft((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    // Filtere nur aktive Components aus dem ausgewählten Template
    const activeServiceProducts = (v.components || []).filter((x) => x.isActive !== false);
    
    const sv = activeServiceProducts.map((x) => {
      const serviceProductId = String(x.contractTemplateId);
      const tb = contractTemplateBlocks.find((t) => String(t.id) === serviceProductId) || null;
      const allowsAccess = x.allowsAccess ?? tb?.allowsAccess ?? true;
      
      // Wert direkt aus dem Component verwenden (nicht aus Tarifblock überschreiben)
      const componentValue = x.value;
      const methodRaw = Number(x.proportionalCalculationMethod ?? tb?.defaultProportionalCalculationMethod ?? 0);
      
      const proportionalValueOverride = x.proportionalValueOverride ?? tb?.defaultProportionalValueOverride ?? null;
      
      return {
        ...x,
        contractTemplateId: serviceProductId,
        contractTemplateName: String(x.contractTemplateName || ""),
        // Legacy support
        serviceProductId,
        serviceProductName: String(x.contractTemplateName || ""),
        paymentCycleValue: x.paymentCycleValue ?? tb?.defaultPaymentCycleValue ?? null,
        paymentCycleUnit: x.paymentCycleUnit ?? tb?.defaultPaymentCycleUnit ?? null,
        // Wert direkt aus dem Component verwenden - NICHT durch Tarifblock überschreiben
        // ...x kopiert bereits den Wert aus dem Component, aber wir setzen ihn explizit, um sicherzustellen, dass er nicht überschrieben wird
        value: componentValue != null && componentValue !== undefined ? componentValue : (tb?.defaultPrice ?? null),
        allowProportionalValueOverrideOnCreate: x.allowProportionalValueOverrideOnCreate !== false,
        renewalValue: x.renewalValue ?? tb?.defaultRenewalValue ?? null,
        renewalUnit: x.renewalUnit ?? tb?.defaultRenewalUnit ?? null,
        usageLimitValue: x.usageLimitValue ?? tb?.defaultUsageLimitValue ?? null,
        usageLimitUnit: x.usageLimitUnit ?? tb?.defaultUsageLimitUnit ?? null,
        durationValue: x.durationValue ?? tb?.defaultDurationValue ?? null,
        durationUnit: x.durationUnit ?? tb?.defaultDurationUnit ?? null,
        validValue: x.validValue ?? tb?.defaultValidValue ?? null,
        validUnit: x.validUnit ?? tb?.defaultValidUnit ?? null,
        proportionalCalculationMethod: methodRaw > 0 ? methodRaw : null,
        proportionalValueOverride,
        paymentMethod: x.paymentMethod ?? null,
        allowsAccess,
        accessScope: allowsAccess ? (x.accessScope ?? tb?.defaultAccessScope ?? ACCESS_SCOPE_ANY_STUDIO) : 0,
        isActive: x.isActive !== false,
        allowedStudioIds: (allowsAccess ? (x.allowedStudioIds || tb?.defaultAllowedStudioIds || []) : []).map(String)
      };
    });
    
    // Sort: Primary first, then normal, then isProportionalFeeBase last (wie im Wizard)
    const sortedServices = [...sv].sort((a, b) => {
      const aIsPrimary = a.isPrimary === true;
      const bIsPrimary = b.isPrimary === true;
      const aIsProportional = a.isProportionalFeeBase === true;
      const bIsProportional = b.isProportionalFeeBase === true;
      const orderA = aIsPrimary ? 0 : aIsProportional ? SERVICE_ORDER_PLACEHOLDER : 1;
      const orderB = bIsPrimary ? 0 : bIsProportional ? SERVICE_ORDER_PLACEHOLDER : 1;
      return orderA - orderB;
    });
    
    // Lade Wallet Products aus dem Vertragspaket
    const wallets = (v.walletProducts || [])
      .filter((w) => w.isActive !== false)
      .map((w) => {
        const walletProductId = String(w.walletProductId);
        const wp = walletProducts.find((p) => String(p.id) === walletProductId) || null;
        return {
          ...w,
          walletProductId,
          walletProductName: String(w.walletProductName || wp?.name || ""),
          walletProductType: w.walletProductType ?? (wp?.type != null ? Number(wp.type) : undefined),
          initialBalance: w.initialBalance ?? wp?.defaultBalance ?? 0,
          validValue: w.validValue ?? wp?.defaultValidValue ?? null,
          validUnit: w.validUnit ?? wp?.defaultValidUnit ?? null,
          usageLimitValue: w.usageLimitValue ?? wp?.defaultUsageLimitValue ?? null,
          usageLimitUnit: w.usageLimitUnit ?? wp?.defaultUsageLimitUnit ?? null,
          allowsAccess: w.allowsAccess ?? wp?.allowsAccess ?? true,
          accessScope: w.accessScope ?? wp?.defaultAccessScope ?? ACCESS_SCOPE_ANY_STUDIO,
          allowedStudioIds: (w.allowedStudioIds || wp?.defaultAllowedStudioIds || []).map(String),
          requiresNoArrears: w.requiresNoArrears ?? false,
          isActive: w.isActive !== false
        };
      });
    
    setBundleServiceDraft((prev) => (JSON.stringify(prev) === JSON.stringify(sortedServices) ? prev : sortedServices));
    setBundleWalletDraft((prev) => (JSON.stringify(prev) === JSON.stringify(wallets) ? prev : wallets));
    
    // Initialisiere die Zahlungsart-Felder aus der Vorlage (nested object für Form.Item ["servicePaymentMethod", id])
    const servicePaymentMethod: Record<string, number | null> = {};
    for (const s of sv) {
      servicePaymentMethod[String(s.serviceProductId)] = s.paymentMethod != null ? Number(s.paymentMethod) : null;
    }
    contractForm.setFieldsValue({ servicePaymentMethod });
  }, [selectedVariant?.id, contractForm, contractTemplateBlocks, walletProducts]);

  const optionGroupSelection = React.useMemo<OptionGroupSelectionState>(() => {
    const templateId = String(selectedVariant?.id || "");
    if (!templateId) return EMPTY_OPTION_GROUP_SELECTION;
    return (
      optionGroupOverrides[templateId] ??
      buildDefaultOptionGroupSelection(selectedVariant?.optionGroups || [], contractOptionGroups)
    );
  }, [selectedVariant, contractOptionGroups, optionGroupOverrides]);

  // Bewusst ohne useCallback: Die Funktion wird nur als Prop weitergereicht und steht in keiner
  // Abhängigkeitsliste, eine stabile Identität bringt hier nichts.
  const setOptionGroupSelection = (next: OptionGroupSelectionState) => {
    const templateId = String(selectedVariant?.id || "");
    if (!templateId) return;
    setOptionGroupOverrides((prev) => ({ ...prev, [templateId]: next }));
  };

  const contractTemplateDefaultPriceOf = React.useCallback(
    (contractTemplateId: string) =>
      contractTemplateBlocks.find((t) => String(t.id) === String(contractTemplateId))?.defaultPrice ?? null,
    [contractTemplateBlocks]
  );

  // If lookups (tariff blocks / wallet products) arrive after a variant was selected, fill only missing defaults
  // without overwriting user overrides or values from the variant.
  React.useEffect(() => {
    if (!selectedVariant) return;

    setBundleServiceDraft((prev) =>
      (prev || []).map((x) => {
        const sid = String(x.serviceProductId || "");
        const tb = sid ? contractTemplateBlocks.find((t) => String(t.id) === sid) || null : null;
        if (!tb) return x;
        const allowsAccess = x.allowsAccess ?? tb.allowsAccess ?? true;
        return {
          ...x,
          paymentCycleValue: x.paymentCycleValue ?? tb.defaultPaymentCycleValue ?? null,
          paymentCycleUnit: x.paymentCycleUnit ?? tb.defaultPaymentCycleUnit ?? null,
          // Nur setzen, wenn noch kein Wert vorhanden ist (nicht überschreiben)
          value: x.value != null ? x.value : (tb.defaultPrice ?? null),
          allowProportionalValueOverrideOnCreate:
            x.allowProportionalValueOverrideOnCreate != null ? x.allowProportionalValueOverrideOnCreate : true,
          renewalValue: x.renewalValue ?? tb.defaultRenewalValue ?? null,
          renewalUnit: x.renewalUnit ?? tb.defaultRenewalUnit ?? null,
          usageLimitValue: x.usageLimitValue ?? tb.defaultUsageLimitValue ?? null,
          usageLimitUnit: x.usageLimitUnit ?? tb.defaultUsageLimitUnit ?? null,
          durationValue: x.durationValue ?? tb.defaultDurationValue ?? null,
          durationUnit: x.durationUnit ?? tb.defaultDurationUnit ?? null,
          validValue: x.validValue ?? tb.defaultValidValue ?? null,
          validUnit: x.validUnit ?? tb.defaultValidUnit ?? null,
          proportionalCalculationMethod: x.proportionalCalculationMethod ?? tb.defaultProportionalCalculationMethod ?? null,
          proportionalValueOverride: x.proportionalValueOverride ?? tb.defaultProportionalValueOverride ?? null,
          allowsAccess,
          accessScope: allowsAccess ? (x.accessScope ?? tb.defaultAccessScope ?? ACCESS_SCOPE_ANY_STUDIO) : 0,
          allowedStudioIds: (allowsAccess
            ? (x.allowedStudioIds?.length ? x.allowedStudioIds : (tb.defaultAllowedStudioIds || []))
            : []
          ).map(String)
        };
      })
    );

    setBundleWalletDraft((prev) =>
      (prev || []).map((x) => {
        const wid = String(x.walletProductId || "");
        const wp = wid ? walletProducts.find((w) => String(w.id) === wid) || null : null;
        if (!wp) return x;
        return {
          ...x,
          walletProductName: String(x.walletProductName || wp.name || ""),
          walletProductType: x.walletProductType ?? (wp.type != null ? Number(wp.type) : undefined),
          initialBalance: x.initialBalance ?? wp.defaultBalance ?? 0,
          validValue: x.validValue ?? wp.defaultValidValue ?? null,
          validUnit: x.validUnit ?? wp.defaultValidUnit ?? null,
          usageLimitValue: x.usageLimitValue ?? wp.defaultUsageLimitValue ?? null,
          usageLimitUnit: x.usageLimitUnit ?? wp.defaultUsageLimitUnit ?? null,
          allowsAccess: x.allowsAccess ?? wp.allowsAccess ?? true,
          accessScope: x.accessScope ?? wp.defaultAccessScope ?? ACCESS_SCOPE_ANY_STUDIO,
          allowedStudioIds: x.allowedStudioIds?.length ? x.allowedStudioIds : (wp.defaultAllowedStudioIds || []).map(String)
        };
      })
    );
  }, [selectedVariant?.id, contractTemplateBlocks, walletProducts]);

  const primaryService = React.useMemo(() => {
    // Verwende bundleServiceDraft wenn vorhanden (wird beim Laden des Templates gesetzt)
    if (bundleServiceDraft && bundleServiceDraft.length > 0) {
      const byPrimary = bundleServiceDraft.find((x) => x.isPrimary === true);
    if (byPrimary) return byPrimary;
      const byMembership = bundleServiceDraft.find((x) => Number(x.serviceProductType ?? x.type ?? 0) === SERVICE_PRODUCT_TYPE_MEMBERSHIP);
      return byMembership || bundleServiceDraft[0] || null;
    }
    // Fallback: Verwende components aus selectedVariant
    const rows = (selectedVariant?.components || []).filter((x) => x.isActive !== false);
    const byPrimary = rows.find((x) => x.isPrimary === true);
    if (byPrimary) return byPrimary;
    const byMembership = rows.find((x) => Number(x.serviceProductType ?? x.type ?? 0) === SERVICE_PRODUCT_TYPE_MEMBERSHIP);
    return byMembership || rows[0] || null;
  }, [bundleServiceDraft, selectedVariant?.components]);

  const installmentPlanMeta = React.useMemo(() => {
    const planId = primaryService?.installmentPaymentPlanId ? String(primaryService.installmentPaymentPlanId) : "";
    if (!planId) return null;
    const min = primaryService?.installmentMinInstallmentCount != null ? Number(primaryService.installmentMinInstallmentCount) : 1;
    const max = primaryService?.installmentMaxInstallmentCount != null ? Number(primaryService.installmentMaxInstallmentCount) : DEFAULT_MAX_INSTALLMENT_COUNT;
    const planName = paymentPlans.find((p) => String(p.id) === planId)?.name || text("Installment payment");
    return { planId, planName, min, max };
  }, [paymentPlans, primaryService?.installmentMaxInstallmentCount, primaryService?.installmentMinInstallmentCount, primaryService?.installmentPaymentPlanId]);

  const primaryDraftRow = React.useMemo(() => {
    if (!primaryService) return null;
    // primaryService kann entweder aus bundleServiceDraft oder aus selectedVariant?.components kommen
    const id = primaryService.serviceProductId
      ? String(primaryService.serviceProductId)
      : primaryService.contractTemplateId
        ? String(primaryService.contractTemplateId)
        : "";
    if (!id) return null;
    // Prüfe zuerst in bundleServiceDraft
    const found = (bundleServiceDraft || []).find((x) => 
      String(x.serviceProductId) === id || String(x.contractTemplateId) === id
    );
    if (found) return found;
    // Wenn nicht in bundleServiceDraft, dann ist primaryService bereits das Component
    return primaryService;
  }, [bundleServiceDraft, primaryService]);

  // Der anteilige Beitrag wird nicht mehr hier berechnet und in den Draft geschrieben.
  // Maßgeblich ist ProportionalCalculationService.CalculateGapProportionalValue im Backend; der
  // Browser zeigt nur eine Vorschau an (calculateProportionalPreview). Das automatische Befüllen
  // von proportionalValueOverride war der Grund, warum das Feld nicht mehr zwischen „vom Menschen
  // gesetzt" und „vom Browser vorgerechnet" unterscheiden konnte — und warum derselbe Abschluss
  // aus der Personenakte einen anderen Betrag ergab als aus dem Abschluss-Widget.

  const selectedPaymentPlan = React.useMemo(() => {
    const planId = installmentPlanMeta?.planId ? String(installmentPlanMeta.planId) : "";
    if (!planId) return null;
    return paymentPlans.find((p) => String(p.id) === planId) || null;
  }, [installmentPlanMeta?.planId, paymentPlans]);

  const installmentPreview = React.useMemo(() => {
    if (!installmentPlanMeta || !selectedPaymentPlan) return null;
    const basePrice = Number(primaryDraftRow?.value ?? selectedVariant?.price ?? 0);
    const count = Number(contractForm.getFieldValue("paymentPlanInstallmentCount") ?? installmentPlanMeta.min ?? 1);
    const p = computeInstallmentsPreviewWithRounding(selectedPaymentPlan, basePrice, count);
    const cycle = p.count <= 1 ? text("Cycle unit once") : cycleUnitLabel(selectedPaymentPlan.cycleUnit);
    return { ...p, basePrice, cycle };
  }, [
    installmentPlanMeta,
    selectedPaymentPlan,
    primaryDraftRow?.value,
    selectedVariant?.price,
    watchedInstallmentCount,
    computeInstallmentsPreviewWithRounding,
    text,
    contractForm
  ]);

  const computeStartFromPreset = React.useCallback((presetType?: number | null, dayOfMonth?: number | null) => {
    const p = presetType == null ? 1 : Number(presetType);
    const now = dayjs();
    if (p === START_PRESET_NEXT_DAY) return now.add(1, "day").startOf("day");
    if (p === START_PRESET_NEXT_MONTH_START) return now.date() === 1 ? now.startOf("day") : now.add(1, "month").startOf("month");
    if (p === START_PRESET_DAY_OF_MONTH) {
      const want = Math.max(1, Math.min(MAX_DAY_OF_MONTH, Number(dayOfMonth ?? 1)));
      const thisMonthDays = now.daysInMonth();
      const dThis = Math.min(want, thisMonthDays);
      const candidate = now.date(dThis).startOf("day");
      if (now.date() <= dThis) return candidate;
      const next = now.add(1, "month");
      const dNext = Math.min(want, next.daysInMonth());
      return next.date(dNext).startOf("day");
    }
    return now.startOf("day");
  }, []);

  // Apply variant-level date presets to the contract bundle drawer.
  // IMPORTANT: When switching the contract bundle, dependent date fields must be refreshed.
  React.useEffect(() => {
    const v = selectedVariant;
    if (!v) return;
    const contractPreset = Number(v.defaultContractStartPreset ?? 1);
    const contractDay = v.defaultContractStartDayOfMonth ?? null;
    const contractStart = computeStartFromPreset(contractPreset, contractDay);
    const today = dayjs().startOf("day");

    contractForm.setFieldsValue({
      startDate: contractStart,
      trainingStartDate: today,
      closingDate: today,
      paymentPlanInstallmentCount: installmentPlanMeta ? installmentPlanMeta.min : null
    });
    setContractCreateWatchedValues((prev) => ({
      ...prev,
      startDate: contractStart,
      trainingStartDate: today,
      paymentPlanInstallmentCount: installmentPlanMeta ? installmentPlanMeta.min : null
    }));
  }, [computeStartFromPreset, contractForm, selectedVariant?.id, installmentPlanMeta]);

  // If installment settings become available/changed (e.g. after loading payment plans), keep the current value in range.
  React.useEffect(() => {
    if (!installmentPlanMeta) return;
    const n = watchedInstallmentCount == null ? null : Number(watchedInstallmentCount);
    if (!n || Number.isNaN(n) || n < installmentPlanMeta.min || n > installmentPlanMeta.max) {
      contractForm.setFieldsValue({ paymentPlanInstallmentCount: installmentPlanMeta.min });
      setContractCreateWatchedValues((prev) => ({
        ...prev,
        paymentPlanInstallmentCount: installmentPlanMeta.min
      }));
    }
  }, [contractForm, installmentPlanMeta, watchedInstallmentCount]);

  const selectedVariantMeta = React.useMemo(() => {
    const vid = String(watchedVariantId || "");
    return vid ? contractVariantMetaById.get(vid) ?? null : null;
  }, [contractVariantMetaById, watchedVariantId]);

  // Tab-Items werden immer berechnet (vor etwaigen Early-Returns), damit die Hook-Anzahl pro Render konstant bleibt (React Rules of Hooks, Fehler #310).
  // Die Tab-Children sind IIFEs mit Hooks; würden wir vorher mit "if (onBack && loading) return …" aussteigen, hätte der nächste Render mehr Hooks.
  const tabItems = [
            {
              key: "overview",
              label: text("Overview"),
              children: (
                <PersonOverviewTab
                  personId={personId}
                  person={person}
                  auth={auth}
                  form={form}
                  text={text}
                  units={units}
                  modal={modal}
                  message={feedback}
                  saving={saving}
                  allowedStudios={allowedStudios}
                  openItemsStudioIdsParam={openItemsStudioIdsParam}
                  paymentMethodNameByType={paymentMethodNameByType}
                  canReadContracts={canReadContracts}
                  canReadFinance={canReadFinance}
                  canReadOpenItems={canReadOpenItems}
                  canReadPayments={canReadPayments}
                  canReadCheckins={canReadCheckins}
                  canReadAccessMedia={canReadAccessMedia}
                  canWriteAccessMedia={canWriteAccessMedia}
                  canWritePerson={canWritePerson}
                  canBlockCheckIn={canBlockCheckIn}
                  accessMediaRef={accessMediaRef}
                  accessMediaColumnEditorModal={accessMediaColumnEditor.modal}
                  changeHomeStudio={changeHomeStudio}
                  refreshPerson={refreshPerson}
                  onOpenTab={(tab) => setSp({ tab })}
                  onOpenMemberAppRequest={(requestId) => setSp({ tab: "overview", memberAppRequestId: requestId })}
                  onAddAccessMedia={() => setAccessMediaOpen(true)}
                  layout={overviewLayout}
                />
              )
            },
            {
              key: "profile",
              label: text("Profile"),
              children: (
                <PersonProfileTab
                  loading={loading}
                  form={form}
                  person={person}
                  personId={personId}
                  canWritePerson={canWritePerson}
                  canWriteFinance={canWriteFinance}
                  saving={saving}
                  savePerson={savePerson}
                  auth={auth}
                  message={feedback}
                  modal={modal}
                  text={text}
                  units={units}
                  dateInputFormat={dateInputFormat}
                  load={load}
                  setPersonBankName={setPersonBankName}
                />
              )
            },
            {
              key: "contracts",
              label: text("Contracts"),
              children: (
                <>
                {aggregatorRequirements.isError && <Alert type="error" title={text("Aggregator identities unavailable")}
                  action={<Button onClick={() => aggregatorRequirements.refetch()}>{text("Retry")}</Button>} />}
                <SettingsListPage<ContractRow>
                  storageKey="persons.detail.contracts"
                  rowKey={(r) =>
                    r.sourceContractPackageId != null
                      ? `sub-${String(r.id ?? "")}`
                      : `pkg-${String(r.id ?? "")}`
                  }
                  request={loadContractRows}
                  expandable={{
                    // Packages with multiple blocks keep their existing tree expansion; the link belongs to the matching child.
                    rowExpandable: row => !row.children?.length && !!aggregatorRequirements.data?.some(x => x.contractId === row.id || (!row.sourceContractPackageId && x.packageId === row.id)),
                    render: row => <ContractAggregatorSection personId={personId} requirements={(aggregatorRequirements.data ?? [])
                      .filter(x => x.contractId === row.id || (!row.sourceContractPackageId && x.packageId === row.id))} />
                  }}
                  columnDefinitions={PERSON_CONTRACT_COLUMN_DEFINITIONS}
                  allColumnsMap={contractsAllColumnsMap}
                  tableClassName="persons-contracts-table"
                  headerTitle={<Typography.Title level={5} style={{ margin: 0 }}>{text("Contract packages")}</Typography.Title>}
                  toolbarExtra={useStackedToolbar ? undefined : (
                    <Flex className="person-detail-toolbar-switch" justify="flex-start" align="center" gap={8} style={{ minWidth: 0, width: isMobile ? "100%" : undefined }}>
                      <Typography.Text style={{ minWidth: 0 }}>{text("Historical contracts")}</Typography.Text>
                      <Switch
                        checked={showHistoricalContracts}
                        onChange={(checked) => {
                          setShowHistoricalContracts(checked);
                          setTimeout(() => contractsRef.current?.reload?.(), 0);
                        }}
                      />
                    </Flex>
                  )}
                  toolbarRightExtra={(useStackedToolbar || canCloseContracts) ? (
                    <Flex
                      wrap="wrap"
                      gap={8}
                      justify="flex-end"
                      align={isMobile ? "stretch" : "center"}
                      style={{ width: isMobile ? "100%" : undefined, minWidth: 0 }}
                    >
                      {useStackedToolbar ? (
                        <Button icon={<FilterOutlined />} onClick={() => setContractFilterDrawerOpen(true)}>
                          {text("Filter")}
                        </Button>
                      ) : null}
                      {canCloseContracts ? (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => onAction("contractBundle")}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {text("Add contract package")}
                        </Button>
                      ) : null}
                    </Flex>
                  ) : undefined}
                  actionsColumn={{
                    key: "actions",
                    valueType: "option",
                    slotCount: 3,
                    className: "persons-contracts-actions-cell",
                  }}
                  actionSlots={contractsActionSlots}
                  actionRef={contractsRef}
                  pagination={{ pageSize: 10 }}
                  columnEditorStorageKey="persons-detail-contracts-columns"
                  rowClassName={(record) => !isContractPackageRow(record) ? "persons-contracts-child-row" : ""}
                  onRowClick={(record) => onContractsTableRowDoubleClick(record)}
                />
                <FormDrawer mode="retained" dirty={false}
                  title={"Filter"}
                  open={contractFilterDrawerOpen}
                  onCancel={() => setContractFilterDrawerOpen(false)}

                  cancelText={"Close"}
                  autoFocusFirstField={false}
                >
                  <Flex className="person-detail-toolbar-switch" justify="space-between" align="center" gap={8} style={{ minWidth: 0, width: "100%" }}>
                    <Typography.Text style={{ minWidth: 0 }}>{text("Historical contracts")}</Typography.Text>
                    <Switch
                      checked={showHistoricalContracts}
                      onChange={(checked) => {
                        setShowHistoricalContracts(checked);
                        setTimeout(() => contractsRef.current?.reload?.(), 0);
                      }}
                    />
                  </Flex>
                </FormDrawer>

                  {canWriteContracts && (
                  <FormDrawer mode="edit" dirty={contractEditDirty}
                    title={contractEditSingleBlock ? "Edit contract package and block" : "Edit contract package"}
                    open={contractEditOpen}
                    onCancel={closeContractPackageEdit}
                    submitting={contractEditSaving}
                    onSave={saveContractPackageEdit}
                    extra={
                      <>
                        <Checkbox checked={contractEditIsLocked === true} onChange={({ target: { checked: val } }) => setContractEditIsLocked(val)}>{text("locked")}</Checkbox>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          disabled={!contractEdit || bookedContractIds.has(String(contractEdit?.id || ""))}
                          onClick={deleteContractPackageEdit}
                        >
                          {text("Delete")}
                        </Button>
                      </>
                    }
                  >
                    <Form
                      form={contractEditForm}
                      layout="vertical"
                      requiredMark={false}
                      onValuesChange={(changedValues) => {
                        if (Object.prototype.hasOwnProperty.call(changedValues, "paymentMethod")) {
                          const value = changedValues.paymentMethod;
                          setContractEditPaymentMethod(value != null ? Number(value) : null);
                        }
                      }}
                    >
                      <Form.Item
                        name="planName"
                        label={text("Contract package display name")}
                        rules={[{ required: true, message: text("Contract package name required") }]}
                      >
                        <Input allowClear placeholder={text("Contract package name placeholder")} />
                      </Form.Item>
                      {contractEditSingleBlock ? (
                        <>
                          <FormSectionHeading title={text("Contract block")} sectionKey="person-detail.contract-block">
                            <Form.Item
                              name="contractBlockName"
                              label={text("Contract block name")}
                              rules={[{ required: true, message: text("Name is required") }]}
                            >
                              <Input allowClear />
                            </Form.Item>
                          </FormSectionHeading>
                        </>
                      ) : null}
                      <Form.Item name="studioId" label={text("Studio")}>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          allowClear
                          options={allowedStudios.map((s) => ({ value: s.id, label: s.name }))}
                        />
                      </Form.Item>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: spacing.md }}>
                        <Form.Item name="startDate" label={text("Start date")} rules={[{ required: true }]}>
                          <DatePicker allowClear={false} format={dateInputFormat} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="endDate" label={text("End date")}>
                          <DatePicker allowClear format={dateInputFormat} style={{ width: "100%" }} />
                        </Form.Item>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: spacing.md }}>
                        {contractEditSingleBlock ? (
                          <Form.Item name="contractBlockValue" label={text("Price")}>
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              step={0.01}
                              decimalSeparator={decimalSeparator}
                              addonAfter={currencyCode}
                              placeholder={moneyPlaceholder}
                            />
                          </Form.Item>
                        ) : (
                          <Form.Item label={text("Payment cycle")}>
                            <Select
                              disabled
                              value={contractEdit?.billingCycle ?? 1}
                              options={billingCycleOptions(text)}
                            />
                          </Form.Item>
                        )}
                        <Form.Item name="closingDate" label={text("Closing date")}>
                          <DatePicker disabled format={dateInputFormat} style={{ width: "100%" }} />
                        </Form.Item>
                      </div>
                      <Form.Item name="paymentMethod" hidden>
                        <Select
                          style={{ width: "100%" }}
                          options={paymentMethodSelectOptions}
                          allowClear
                          placeholder={contractEditHasDifferentPaymentMethods ? text("(different)") : undefined}
                        />
                      </Form.Item>

                      <Form.Item shouldUpdate noStyle>
                        {() => {
                          const paymentMethod = contractEditForm.getFieldValue("paymentMethod");
                          // Zeige Lastschriftgruppe nur an, wenn Zahlungsmethode SEPA ist
                          // ABER: Das Formular-Feld muss immer existieren, auch wenn nicht sichtbar
                          const isSepa = Number(paymentMethod) === PAYMENT_METHOD_SEPA;
                          return (
                            <Form.Item
                              name="sepaGroupId"
                              label={text("SEPA group")}
                              hidden={!isSepa}
                            >
                              <Select
                                allowClear
                                placeholder={sepaGroups.length === 0 ? text("No groups available") : text("None")}
                                options={sepaGroups.map((g) => ({ value: g.id, label: g.name }))}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                      <Form.Item name="restPeriodTemplateId" label={text("Rest period model")}>
                        <Select
                          disabled
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder={text("Select rest period model")}
                          options={restTemplates.map((t) => ({
                            value: t.id,
                            label: t.isActive ? t.name : `${t.name} (${text("Inactive")})`
                          }))}
                        />
                      </Form.Item>
                      <Form.Item name="cancellationModelId" label={text("Cancellation model")}>
                        <Select
                          disabled
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder={text("Select cancellation model")}
                          options={cancellationModels.map((m) => ({
                            value: m.id,
                            label: m.name
                          }))}
                        />
                      </Form.Item>
                      <Form.Item name="note" label={text("Contract package note")}>
                        <Input.TextArea rows={3} placeholder={text("Enter note...")} />
                      </Form.Item>
                    </Form>
                  </FormDrawer>
                  )}

                  {/* Bereich "Leistungen/Bausteine" entfernt (redundant zu Vertrags-/Bausteinliste) */}
                </>
              )
            },
            {
              key: "restPeriods",
              label: text("Rest periods"),
              children: (
                <SettingsListPage<RestPeriodRow>
                    toolbarRightExtra={restPeriodsController.table.headerExtra}
                    storageKey="persons.detail.restPeriods"
                    rowKey="id"
                    actionRef={restPeriodsController.actionRef}
                    request={restPeriodsController.table.request}
                    columnDefinitions={restPeriodsController.table.columnDefinitions}
                    allColumnsMap={restPeriodsController.table.allColumnsMap as Record<string, ProColumnsWithMeasure<RestPeriodRow>>}
                    actionsColumn={restPeriodsController.table.actionsColumn}
                    toolBarActions={[]}
                    onRowClick={restPeriodsController.table.onRowClick}
                    pagination={restPeriodsController.table.pagination}
                  />
              )
            },
            {
              key: "priceAdjustments",
              label: text("Price adjustments"),
              children: (
                <>
                <SettingsListPage<ContractPriceAdjustmentRow>
                    toolbarRightExtra={canWritePriceAdjustments ? (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={priceAdjustmentController.openCreate}
                      >
                        {text("Add price adjustment")}
                      </Button>
                    ) : undefined}
                    storageKey="persons.detail.contractPriceAdjustments"
                    rowKey="id"
                    request={priceAdjustmentController.request}
                    columnDefinitions={priceAdjustmentController.listConfig.columnDefinitions}
                    allColumnsMap={priceAdjustmentController.listConfig.allColumnsMap}
                    actionsColumn={canWritePriceAdjustments ? priceAdjustmentController.listConfig.actionsColumn : undefined}
                    toolBarActions={[]}
                    onRowClick={canWritePriceAdjustments ? priceAdjustmentController.openEdit : undefined}
                    actionRef={priceAdjustmentController.actionRef}
                    pagination={{ pageSize: 20 }}
                  />
                <FormDrawer mode="edit"
                    title={priceAdjustmentController.edit ? text("Edit price adjustment") : text("Add price adjustment")}
                    open={priceAdjustmentController.open}
                    dirty={priceAdjustmentController.dirty}
                    onCancel={priceAdjustmentController.close}
                    submitting={priceAdjustmentController.saving}
                    contentLoading={priceAdjustmentController.targetsLoading}
                    submitDisabled={priceAdjustmentController.targetsLoading}
                    onSave={priceAdjustmentController.save}
                  >
                    <Form form={priceAdjustmentController.form} layout="vertical" requiredMark={false}>
                      <Form.Item
                        name="contractPackageId"
                        label={text("Contract package")}
                        rules={
                          priceAdjustmentController.edit
                            ? []
                            : [{ required: true, message: text("Please select contract package.") }]
                        }
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder={text("Select contract package")}
                          disabled={!!priceAdjustmentController.edit}
                          options={priceAdjustmentController.contractPackageOptions}
                          onChange={(pkgId) => {
                            if (!pkgId || priceAdjustmentController.edit) return;
                            priceAdjustmentController.applyPackageSelection(String(pkgId));
                          }}
                        />
                      </Form.Item>
                      <Form.Item name="contractId" label={text("Contract block")} rules={[{ required: true, message: text("Contract is required") }]}>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder={text("Select contract block")}
                          disabled={!priceAdjustmentController.edit && !priceAdjustmentController.selectedContractPackageId}
                          options={priceAdjustmentController.contractsForSelectedPackage.map((c) => ({
                            value: c.id,
                            label: `${c.name || ""} (${formatDateOnly(String(c.startDate || "").slice(0, ISO_DATE_LENGTH), units)})`
                          }))}
                          onChange={(contractId) => {
                            if (!contractId || priceAdjustmentController.edit) return;
                            priceAdjustmentController.applyContractSelection(String(contractId));
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="manualAdjustmentName"
                        label={text("Price adjustment designation")}
                        rules={canCustomizePriceAdjustments ? [{ required: true, message: text("Manual price adjustment name is required") }] : []}
                      >
                        <Input disabled={!canCustomizePriceAdjustments} placeholder={text("Manual price adjustment name placeholder")} />
                      </Form.Item>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: spacing.md }}>
                        <Form.Item
                          name="startDate"
                          label={text("From")}
                          rules={[{ required: true, message: text("Start date is required") }]}
                        >
                          <DatePicker
                            allowClear={false}
                            format={dateInputFormat}
                            style={{ width: "100%" }}
                            disabledDate={(d) => {
                              if (!d) return true;
                              const contractId = priceAdjustmentController.form.getFieldValue("contractId");
                              const contract = contractId ? priceAdjustmentController.individualContracts.find((c) => c.id === contractId) : null;
                              if (!contract) return false;
                              return !priceAdjustmentController.isValidPeriodStart(contract, d);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          name="endDate"
                          label={text("Until")}
                          rules={[
                            {
                              validator: (_, value) => {
                                const start = priceAdjustmentController.form.getFieldValue("startDate");
                                if (!value || !start) return Promise.resolve();
                                const startD = dayjs(start);
                                const endD = dayjs(value);
                                if (endD.isBefore(startD, "day") || endD.isSame(startD, "day")) {
                                  return Promise.reject(new Error(text("End date must be after start date")));
                                }
                                return Promise.resolve();
                              }
                            }
                          ]}
                        >
                          <DatePicker
                            allowClear
                            format={dateInputFormat}
                            style={{ width: "100%" }}
                            disabledDate={(d) => {
                              if (!d) return false;
                              const startDate = priceAdjustmentController.form.getFieldValue("startDate");
                              if (!startDate) return false;
                              return d.isBefore(dayjs(startDate).startOf("day"), "day") || d.isSame(dayjs(startDate).startOf("day"), "day");
                            }}
                          />
                        </Form.Item>
                      </div>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.startDate !== cur.startDate}>
                        {({ getFieldValue }) => {
                          if (priceAdjustmentController.edit) return null;
                          const startDate = getFieldValue("startDate");
                          const start = startDate ? dayjs(startDate).startOf("day") : null;
                          if (!start?.isValid() || !start.isBefore(dayjs().startOf("day"), "day")) return null;
                          return (
                            <Alert
                              type="warning"
                              showIcon
                              title={text("Price adjustment starts in the past warning")}
                              style={{ marginBottom: spacing.lg }}
                            />
                          );
                        }}
                      </Form.Item>
                      <Form.Item
                        name="priceAdjustmentId"
                        label={canCustomizePriceAdjustments ? text("Price adjustment model") : text("Price adjustment")}
                        rules={canCustomizePriceAdjustments ? [] : [{ required: true, message: text("Price adjustment is required") }]}
                      >
                        <Select
                          allowClear={canCustomizePriceAdjustments}
                          showSearch
                          optionFilterProp="label"
                          placeholder={canCustomizePriceAdjustments ? text("Select price adjustment or configure individually…") : text("Select price adjustment")}
                          options={priceAdjustmentController.priceAdjustmentOptions}
                          onChange={(value) => {
                            if (value) priceAdjustmentController.applyPriceAdjustmentDefinition(String(value));
                          }}
                        />
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.manualAdjustmentType !== cur.manualAdjustmentType}>
                        {({ getFieldValue }) => {
                          const manualType = Number(getFieldValue("manualAdjustmentType") || PRICE_ADJUSTMENT_TYPE_PERCENTAGE);
                          return (
                            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(3, minmax(0, 1fr))", gap: spacing.md }}>
                              <Form.Item name="manualAdjustmentType" label={text("Type")} rules={canCustomizePriceAdjustments ? [{ required: true, message: text("Required") }] : []}>
                                <Select
                                  disabled={!canCustomizePriceAdjustments}
                                  options={[
                                    { value: PRICE_ADJUSTMENT_TYPE_FIXED, label: text("Fixed amount") },
                                    { value: PRICE_ADJUSTMENT_TYPE_PERCENTAGE, label: text("Percentage") },
                                    { value: PRICE_ADJUSTMENT_TYPE_SET_PRICE, label: text("Target price") }
                                  ]}
                                />
                              </Form.Item>
                              <Form.Item
                                name="manualAdjustmentValue"
                                label={text("Adjustment")}
                                rules={canCustomizePriceAdjustments ? [
                                  { required: true, message: text("Adjustment value is required") },
                                  {
                                    validator: (_, value) => {
                                      if (manualType === PRICE_ADJUSTMENT_TYPE_PERCENTAGE && Number(value) < MIN_PERCENT_ADJUSTMENT) {
                                        return Promise.reject(new Error(text("Percentage reduction cannot exceed 100%")));
                                      }
                                      if (manualType === PRICE_ADJUSTMENT_TYPE_SET_PRICE && Number(value) < 0) {
                                        return Promise.reject(new Error(text("Target price cannot be negative")));
                                      }
                                      return Promise.resolve();
                                    }
                                  }
                                ] : []}
                              >
                                <InputNumber
                                  disabled={!canCustomizePriceAdjustments}
                                  style={{ width: "100%" }}
                                  step={0.01}
                                  decimalSeparator={decimalSeparator}
                                  addonAfter={manualType === PRICE_ADJUSTMENT_TYPE_PERCENTAGE ? "%" : currencyCode}
                                  placeholder={manualType === PRICE_ADJUSTMENT_TYPE_PERCENTAGE ? "-10" : moneyPlaceholder}
                                />
                              </Form.Item>
                              <Form.Item name="manualAdjustmentCycleUnit" label={text("Cycle")} rules={canCustomizePriceAdjustments ? [{ required: true, message: text("Required") }] : []}>
                                <Select disabled={!canCustomizePriceAdjustments} options={billingCycleOptions(text)} />
                              </Form.Item>
                            </div>
                          );
                        }}
                      </Form.Item>
                      <Form.Item name="note" label={text("Note (optional)")}>
                        <Input.TextArea rows={3} placeholder={text("Enter note...")} />
                      </Form.Item>
                    </Form>
                  </FormDrawer>
                </>
              )
            },
            {
              key: "statement",
              label: text("Account statement"),
              children: (
                <PersonAccountStatementTab
                  api={auth.api}
                  text={text}
                  units={units}
                  personId={personId}
                  studioIdsParam={openItemsStudioIdsParam}
                  canWriteFinance={canWriteFinance}
                  onRecordPayment={openPaymentQuickAction}
                />
              )
            },
            {
              key: "contributions",
              label: text("Contributions"),
              children: (
                <SettingsListPage<ContributionCalendarRow>
                  preserveDesktopTable
                  toolbarRightExtra={contributionsController.table.headerExtra}
                  storageKey="persons.detail.contributions"
                  rowKey="id"
                  dataMode="complete-scope"
                  actionRef={contributionsRef}
                  toolbarExtra={undefined}
                  toolBarActions={[]}
                  rowSelection={contributionsController.table.rowSelection}
                  onRowClickForSelection={contributionsController.table.onRowClickForSelection}
                  columnDefinitions={contributionsController.table.columnDefinitions}
                  allColumnsMap={contributionsController.table.allColumnsMap as Record<string, ProColumnsWithMeasure<ContributionCalendarRow>>}
                  actionsColumn={contributionsController.table.actionsColumn}
                  pagination={contributionsController.table.pagination}
                  request={contributionsController.table.request}
                  onChange={contributionsController.table.onChange}
                />
              )
            },
            {
              key: "wallets",
              label: text("Wallets"),
              children: (
                <PersonWalletsTab
                  api={auth.api}
                  text={text}
                  units={units}
                  personId={personId}
                  canWriteFinance={canWriteFinance}
                  showHistoricalWallets={showHistoricalWallets}
                  setShowHistoricalWallets={setShowHistoricalWallets}
                  walletsRef={walletsRef}
                  onOpenWalletCreate={walletDrawer.openCreate}
                  onOpenWalletEdit={walletDrawer.openEdit}
                />
              )
            },
            {
              key: "openitems",
              label: text("Open Items"),
              children: (
                <PersonOpenItemsTab
                  api={auth.api}
                  text={text}
                  units={units}
                  personId={personId}
                  openItemsStudioIdsParam={openItemsStudioIdsParam}
                  canAgreeInstallment={canAgreeInstallment}
                  canWriteFinance={canWriteFinance}
                  dateInputFormat={dateInputFormat}
                  openItemsRef={openItemsRef}
                  selectedRowKeys={openItemsSelectedRowKeys}
                  setSelectedRowKeys={setOpenItemsSelectedRowKeys}
                  selectedRows={openItemsSelectedRows}
                  setSelectedRows={setOpenItemsSelectedRows}
                  setPaymentDrawerItems={setOpenItemsPaymentDrawerItems}
                  setPaymentDrawerSelectedItems={setOpenItemsPaymentDrawerSelectedItems}
                  setPaymentDrawerOpen={setOpenItemsPaymentDrawerOpen}
                  onOpenContributionEdit={contributionsController.openEdit}
                />
              )
            },
            {
              key: "payments",
              label: text("Payments"),
              children: (
                <PersonPaymentsTab
                  api={auth.api}
                  text={text}
                  units={units}
                  personId={personId}
                  canWriteFinance={canWriteFinance}
                  paymentsRef={paymentsRef}
                  paymentMethodNameByType={paymentMethodNameByType}
                  selectedRowKeys={paymentsSelectedRowKeys}
                  setSelectedRowKeys={setPaymentsSelectedRowKeys}
                  selectedRows={paymentsSelectedRows}
                  setSelectedRows={setPaymentsSelectedRows}
                  setPaymentEdit={setPaymentEdit}
                  paymentEditForm={paymentEditForm}
                  setPaymentEditOpen={setPaymentEditOpen}
                  setPaymentCancellationDrawerOpen={setPaymentCancellationDrawerOpen}
                  setPaymentCancellationDrawerPayments={setPaymentCancellationDrawerPayments}
                />
              )
            },
            {
              key: "articleSales",
              label: text("Articles"),
              children: (
                <PersonArticleSalesTab
                  api={auth.api}
                  message={feedback}
                  text={text}
                  units={units}
                  personId={personId}
                  homeStudioId={person?.homeStudioId ?? null}
                  allowedStudios={allowedStudios}
                  dateInputFormat={dateInputFormat}
                  decimalSeparator={decimalSeparator}
                  currencyCode={currencyCode}
                  moneyPlaceholder={moneyPlaceholder}
                  paymentMethodNameByType={paymentMethodNameByType}
                  canRead={canReadArticleSales}
                  canVoid={canWriteFinance || auth.hasAnyPermission(Permissions.ArticleSalesWrite)}
                  canPay={canWriteFinance}
                  showHistoricalArticleSales={showHistoricalArticleSales}
                  setShowHistoricalArticleSales={setShowHistoricalArticleSales}
                  onPersonUpdated={setPerson}
                />
              ),
            },
            {
              key: "promotions",
              label: text("Promotions & bonuses"),
              children: (
                <PersonPromotionsTab
                  personId={personId}
                  personName={`${person?.firstName || ""} ${person?.lastName || ""}`.trim()}
                  canWrite={canWriteMemberPromotions}
                  canExecute={canExecuteMemberPromotions}
                  dateInputFormat={dateInputFormat}
                  units={units}
                  text={text}
                />
              )
            },
            {
              key: "files",
              label: text("Digital file"),
              children: (
                <PersonDigitalFileTab
                  api={auth.api}
                  text={text}
                  units={units}
                  personId={personId}
                  person={person}
                  allowedStudios={allowedStudios}
                  accountId={auth.info?.accountId}
                  canSendCommunication={canSendCommunication}
                  canWriteDocuments={canWriteDocuments}
                  filesRef={filesRef}
                  timelineDirectionFilter={timelineDirectionFilter}
                  setTimelineDirectionFilter={setTimelineDirectionFilter}
                  timelineTypeFilter={timelineTypeFilter}
                  setTimelineTypeFilter={setTimelineTypeFilter}
                  communicationMode={communicationMode}
                  communicationSaving={communicationSaving}
                  communicationForm={communicationForm}
                  openCommunication={openCommunication}
                  closeCommunication={closeCommunication}
                  sendCommunication={sendCommunication}
                />
              )
            },
            {
              key: "changes",
              label: text("Changelog"),
              children: <PersonChangeHistoryTab auth={auth} personId={personId} text={text} units={units} />
            },
          ];

  const tabVisibility: PersonDetailTabVisibility = {
    hideProspectDetailSections,
    canReadContracts,
    canReadRestPeriods,
    canReadPriceAdjustments,
    canReadFinance,
    canReadOpenItems,
    canReadPayments,
    canReadArticleSales,
    canReadMemberPromotions,
    canReadDocuments,
    canReadChangeHistory
  };
  const { visibleTabItems, activeTabKey } = getPersonDetailTabState(tabItems, tabVisibility, tab);
  const { groups: tabGroups, activeGroupKey } = groupPersonDetailTabs(visibleTabItems, activeTabKey);
  // Einmal geöffnete Bereiche bleiben geladen, wie zuvor die Reiter (kein erneutes Laden beim Zurückwechseln).
  const [visitedTabKeys, setVisitedTabKeys] = React.useState<ReadonlySet<string>>(() => new Set([activeTabKey]));
  if (!visitedTabKeys.has(activeTabKey)) setVisitedTabKeys(new Set(visitedTabKeys).add(activeTabKey));
  const openTabGroup = (groupKey: string) => {
    const group = tabGroups.find((item) => item.key === groupKey);
    if (!group || group.sections.some((section) => String(section.key) === activeTabKey)) return;
    setSp({ tab: String(group.sections[0]!.key) });
  };
  const renderTabGroup = (group: (typeof tabGroups)[number]) => {
    if (group.sections.length === 1) return group.sections[0]!.children;
    const sectionOptions = group.sections.map((section) => ({ value: String(section.key), label: section.label }));
    const activeSection = group.sections.some((section) => String(section.key) === activeTabKey) ? activeTabKey : String(group.sections[0]!.key);
    return (
      <Flex vertical gap="middle" style={{ width: "100%" }}>
        {useCompactTabNavigation
          ? <Select aria-label={text(group.labelKey)} value={activeSection} onChange={(k) => setSp({ tab: String(k) })} options={sectionOptions} style={{ width: "100%" }} />
          : <Segmented aria-label={text(group.labelKey)} value={activeSection} onChange={(k) => setSp({ tab: String(k) })} options={sectionOptions} style={{ alignSelf: "flex-start" }} />}
        {group.sections.map((section) => visitedTabKeys.has(String(section.key)) ? (
          <div key={String(section.key)} hidden={String(section.key) !== activeSection}>{section.children}</div>
        ) : null)}
      </Flex>
    );
  };

  /** Volle Detail-Seite nur mit geladener Person — sonst renden Tab-Inhalte mit Hooks bei person === null und lösen Fehler aus. */
  if (!onBack && personDetailFetchError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 280,
          gap: spacing.lg
        }}
      >
        <Typography.Text type="secondary">{text("The person could not be loaded.")}</Typography.Text>
        <Button type="primary" onClick={() => nav(backTarget, { replace: true })}>
          {text("Back")}
        </Button>
      </div>
    );
  }
  /** Keine Person: die Tab-Struktur kann noch nicht stehen, deshalb ausnahmsweise `placeholder`. */
  if (!onBack && loading && !person) {
    return (
      <>
        <Form form={form} style={{ display: "none" }} />
        <LoadingOverlay visible scope="placeholder" message="Loading…" />
      </>
    );
  }
  if (!onBack && !loading && !person && String(personId).trim()) {
    return (
      <>
        <Form form={form} style={{ display: "none" }} />
        <LoadingOverlay visible scope="placeholder" message="Loading…" />
      </>
    );
  }

  if (onBack && loading) {
    return (
      <>
        <Form form={form} style={{ display: "none" }} />
        <LoadingOverlay visible scope="placeholder" message="Loading…" />
      </>
    );
  }
  if (onBack && !person) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 280, gap: spacing.lg }}>
        <Typography.Text type="secondary">{text("The person could not be loaded.")}</Typography.Text>
        <Button type="primary" onClick={onBack}>{text("Close")}</Button>
      </div>
    );
  }

  const quickActionButtons = person ? (
    <>
      {canWriteFinance && !hideProspectDetailSections ? (
        <Button icon={<DollarOutlined />} onClick={openPaymentQuickAction}>{text("Payment")}</Button>
      ) : null}
      {showContractQuickActions && canWriteRestPeriods ? (
        <Button icon={<PauseCircleOutlined />} onClick={openRestPeriodQuickAction}>{text("Rest period")}</Button>
      ) : null}
    </>
  ) : null;
  const actionsDropdown = actions.length > 0 || quickActionButtons ? (
    <Space wrap>
      {quickActionButtons}
      {actions.length > 0 ? <ActionMenu label={text("Actions")} items={actions as ActionMenuItem[]} onAction={onAction} /> : null}
    </Space>
  ) : null;


  // Zahnrad der Übersicht nur, solange die Übersicht offen ist.
  const overviewLayoutActions = activeGroupKey === TAB_KEY_OVERVIEW ? <PersonOverviewLayoutActions layout={overviewLayout} text={text} /> : null;

  return (
    <>{documentReviewWorkspace}<React.Activity mode={documentReviewWorkspace ? "hidden" : "visible"}>
    <LoadingOverlay visible={!onBack && loading && !!person} message="Loading…">
    <div>
      {/* Kopf als Bühne des Stils (Leuchten, Titelschrift): app/theme/styles/base.css „person-detail-hero" */}
      <Flex className="person-detail-hero ck-on-dark" justify="space-between" align="flex-start" gap={12} wrap="wrap">
        <div style={{ display: "flex", gap: isMobile ? 10 : 16, alignItems: "flex-start", minWidth: 0, width: isMobile ? "100%" : undefined }}>
          {/* design-rule-exception: image-upload-draft Personenfoto ist eine eigene Sofortaktion ohne Formular und ohne Abbrechen */}
          <ImageCaptureField
            key={personId}
            readOnly={!canWritePerson}
            labels={personPhotoController.labels}
            imageUrl={personPhotoController.photoObjectUrl}
            dimensions={isMobile ? { width: 96, height: 72 } : { width: 160, height: 120 }}
            accentColor={checkInEligibilityColor(checkInEligibility, token)}
            showActions={false}
            uploading={personPhotoController.uploading}
            captureLimit={personPhotoController.captureLimit}
            onImageSelected={personPhotoController.handleFileUpload}
            onError={personPhotoController.onError}
          />
          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <Tooltip title={isMobile ? backLabel : undefined}>
              <Button
                type="text"
                size="small"
                icon={<ArrowLeftOutlined />}
                aria-label={backLabel}
                onClick={() => { if (onBack) return onBack(); else nav(backTarget); }}
                style={{ paddingLeft: 0 }}
              >
                {isMobile ? null : backLabel}
              </Button>
            </Tooltip>
            <Typography.Title className="person-detail-hero-title" level={isMobile ? 4 : 3} style={{ margin: 0, overflowWrap: "anywhere" }} data-page-heading="primary">
              {person ? `${person.firstName || ""} ${person.lastName || ""}`.trim() || text("(no name)") : text("Person")}
            </Typography.Title>
            <div style={{ marginTop: spacing.sm, display: "flex", gap: spacing.sm, flexWrap: "wrap", alignItems: "center" }}>
              {person ? lifecycleTag(person.lifecycleStatus, person.isCheckInBlocked === true, text, person.gender) : null}
              {person?.privacyStatus === PRIVACY_STATUS.anonymized ? (
                <Tag color="default">{text("anonymized")}</Tag>
              ) : person?.privacyStatus === PRIVACY_STATUS.erasureRequested ? (
                <Tag color="orange">{text("Erasure requested")}</Tag>
              ) : null}
              {activeCheckInAt && (
                <Tag color="green">{text("Checked in")}</Tag>
              )}
            </div>
          </div>
        </div>
        {!isMobile && actionsDropdown ? actionsDropdown : null}
      </Flex>
      {isMobile && actionsDropdown ? (
        <Flex justify="flex-end" align="center" style={{ marginTop: designSpacing.controlGap, width: "100%" }}>
          {actionsDropdown}
        </Flex>
      ) : null}

      {activeTabKey !== TAB_KEY_PROFILE ? <Form form={form} style={{ display: "none" }} /> : null}
      {!contractOpen ? <Form form={contractForm} style={{ display: "none" }} /> : null}
      {!contractEditOpen ? <Form form={contractEditForm} style={{ display: "none" }} /> : null}
      {!serviceEditOpen ? <Form form={serviceEditForm} style={{ display: "none" }} /> : null}
      {!priceAdjustmentController.open ? <Form form={priceAdjustmentController.form} style={{ display: "none" }} /> : null}
      {communicationMode !== "create" ? <Form form={communicationForm} style={{ display: "none" }} /> : null}
      {!walletDrawer.open ? <Form form={walletDrawer.form} style={{ display: "none" }} /> : null}
      {!paymentEditOpen ? <Form form={paymentEditForm} style={{ display: "none" }} /> : null}
      {!contributionsController.editOpen ? <Form form={contributionsController.editForm} style={{ display: "none" }} /> : null}
      {!contributionsController.bulkPayState.open ? <Form form={contributionsController.bulkPayState.form} style={{ display: "none" }} /> : null}

      {person ? (
        <PersonPrivacyDrawer
          open={privacyDrawerOpen}
          personId={personId}
          personName={`${person.firstName || ""} ${person.lastName || ""}`.trim()}
          api={auth.api}
          units={units}
          canManage={canManagePrivacy}
          onClose={() => {
            setPrivacyDrawerOpen(false);
            if (privacyRequestIdParam) {
              const next = new URLSearchParams(sp);
              next.delete("privacyRequestId");
              setSp(next);
            }
          }}
          onPersonChanged={async () => {
            const updated = await refreshPerson();
            void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.detail(personId) });
            if (updated) {
              accessMediaRef.current?.reload?.();
              void filesRef.current?.revalidateCurrentQuery();
            }
          }}
        />
      ) : null}

      <div style={{ marginTop: spacing.lg }}>
        {useCompactTabNavigation ? (
          <Flex vertical gap="middle" style={{ width: "100%" }}>
            <Flex gap="small" align="center">
              <Select
                value={activeGroupKey}
                onChange={(k) => openTabGroup(String(k))}
                style={{ flex: "1 1 auto", minWidth: 0 }}
                options={tabGroups.map((group) => ({
                  value: group.key,
                  label: text(group.labelKey)
                }))}
              />
              {overviewLayoutActions}
            </Flex>
            {(() => {
              const activeGroup = tabGroups.find((group) => group.key === activeGroupKey);
              return activeGroup ? renderTabGroup(activeGroup) : null;
            })()}
          </Flex>
        ) : (
          <Tabs
            className="person-detail-tabs"
            activeKey={activeGroupKey}
            onChange={(k) => openTabGroup(String(k))}
            destroyOnHidden={false}
            tabBarExtraContent={overviewLayoutActions}
            items={tabGroups.map((group) => ({ key: group.key, label: text(group.labelKey), children: renderTabGroup(group) }))}
          />
        )}
      </div>

      {/* Außerhalb der Segmente: Vorgangsleiste und App-Anfragen öffnen ihn auch, bevor das Segment „Ruhezeiten“ besucht wurde. */}
      {canReadRestPeriods && !hideProspectDetailSections && (
        <RestPeriodDrawer
          open={restPeriodsController.open}
          onClose={restPeriodsController.close}
          personId={personId}
          editRestPeriod={restPeriodsController.edit}
          prefill={restPeriodsController.prefill ?? undefined}
          memberAppRequestId={memberAppRequestIdForDrawer}
          onOpenDocumentReview={openDocumentReview}
          personEmail={person?.email}
          studios={allowedStudios}
          contracts={contracts}
        />
      )}

      {canWriteContracts && (
        <PersonServiceEditDrawer
          personId={personId}
          open={serviceEditOpen}
          serviceEdit={serviceEdit}
          form={serviceEditForm}
          isLocked={serviceEditIsLocked}
          setIsLocked={setServiceEditIsLocked}
          sepaGroups={sepaGroups}
          paymentMethodSelectOptions={paymentMethodSelectOptions}
          cycleUnitSelectOptions={cycleUnitSelectOptionsForServiceEdit}
          dateInputFormat={dateInputFormat}
          decimalSeparator={decimalSeparator}
          currencyCode={currencyCode}
          moneyPlaceholder={moneyPlaceholder}
          units={units}
          api={auth.api}
          modal={modal}
          message={feedback}
          text={text}
          onCancel={closeServiceEdit}
          onSaved={markServiceEditSaved}
          onDeleted={markServiceEditSaved}
        />
      )}

      {canCloseContracts && (
        <PersonContractCreateDrawer
          open={contractOpen}
          form={contractForm}
          person={person}
          personId={personId}
          allowedStudios={allowedStudios}
          accounts={accounts}
          closingAccountId={closingAccountId}
          setClosingAccountId={setClosingAccountId}
          contractVariantOptions={contractVariantOptions}
          selectedVariant={selectedVariant}
          selectedVariantMeta={selectedVariantMeta}
          bundleServiceDraft={bundleServiceDraft}
          setBundleServiceDraft={setBundleServiceDraft}
          bundleWalletDraft={bundleWalletDraft}
          contractOptionGroups={contractOptionGroups}
          optionGroupSelection={optionGroupSelection}
          setOptionGroupSelection={setOptionGroupSelection}
          contractTemplateDefaultPriceOf={contractTemplateDefaultPriceOf}
          watchedContractStartDate={watchedContractStartDate}
          watchedTrainingStartDate={watchedTrainingStartDate}
          primaryService={primaryService}
          installmentPlanMeta={installmentPlanMeta}
          selectedPaymentPlan={selectedPaymentPlan}
          installmentPreview={installmentPreview}
          walletProducts={walletProducts}
          paymentMethodSelectOptions={paymentMethodSelectOptions}
          canUseMemberPromotions={canWriteMemberPromotions}
          dateInputFormat={dateInputFormat}
          decimalSeparator={decimalSeparator}
          currencyCode={currencyCode}
          moneyPlaceholder={moneyPlaceholder}
          units={units}
          api={auth.api}
          modal={modal}
          text={text}
          billingCycleLabel={billingCycleLabel}
          cycleUnitLabel={cycleUnitLabel}
          usageLimitUnitLabel={usageLimitUnitLabel}
          timeUnitLabel={timeUnitLabel}
          accessScopeLabel={accessScopeLabel}
          canChangeHomeStudio={auth.hasAnyPermission(Permissions.PersonsChangeHomeStudio)}
          onCancel={() => {
            setContractOpen(false);
            setOptionGroupOverrides({});
          }}
          onValuesChange={syncContractCreateWatchedValues}
          onChangeHomeStudio={async (homeStudioId) => {
            if (!person) return;
            await changeHomeStudio(homeStudioId);
          }}
          onCreated={async () => {
            setOptionGroupOverrides({});
            feedback.success(text("Contract package added."));
            setContractOpen(false);
            setClosingAccountId("");
          }}
        />
      )}

      <PersonWalletEditDrawer
        canWriteFinance={canWriteFinance}
        open={walletDrawer.open}
        walletEdit={walletDrawer.edit}
        form={walletDrawer.form}
        saving={walletDrawer.saving}
        setSaving={walletDrawer.setSaving}
        isLocked={walletDrawer.isLocked}
        setIsLocked={walletDrawer.setIsLocked}
        walletProducts={walletProducts}
        walletBalanceTouched={walletDrawer.balanceTouched}
        setWalletBalanceTouched={walletDrawer.setBalanceTouched}
        selectedWalletProduct={walletDrawer.selectedWalletProduct}
        onWalletProductChange={walletDrawer.setSelectedWalletProductId}
        adjustAmount={walletDrawer.adjustAmount}
        setAdjustAmount={walletDrawer.setAdjustAmount}
        history={walletDrawer.history}
        historyLoading={walletDrawer.historyLoading}
        api={auth.api}
        message={feedback}
        modal={modal}
        text={text}
        units={units}
        personId={personId}
        homeStudioId={person?.homeStudioId ?? null}
        decimalSeparator={decimalSeparator}
        currencyCode={currencyCode}
        moneyPlaceholder={moneyPlaceholder}
        dateInputFormat={dateInputFormat}
        onClose={walletDrawer.close}
        onDeleted={walletDrawer.handleDeleted}
        onSaved={(updatedPerson) => {
          void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.wallets(personId) });
          void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.detail(personId) });
          walletDrawer.handleSaved(updatedPerson);
        }}
        onReloadWalletAndHistory={walletDrawer.refetchWalletAndHistory}
        onReloadWalletsAfterBalanceAdjustment={() => {
          void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.wallets(personId) });
          walletsRef.current?.reload?.();
        }}
      />

      <PersonPaymentRecordDrawers
        canWriteFinance={canWriteFinance}
        api={auth.api}
        text={text}
        personId={personId}
        studioId={person?.homeStudioId ?? undefined}
        openItemsStudioIdsParam={openItemsStudioIdsParam}
        units={units}
        paymentsState={{
          open: paymentsPaymentDrawerOpen,
          items: paymentsPaymentDrawerItems,
          selectedItems: paymentsPaymentDrawerSelectedItems,
          setOpen: setPaymentsPaymentDrawerOpen,
          setSelectedItems: setPaymentsPaymentDrawerSelectedItems
        }}
        contributionsState={{
          open: contributionsController.paymentDrawerState.open,
          items: contributionsController.paymentDrawerState.items,
          selectedItems: contributionsController.paymentDrawerState.selectedItems,
          setOpen: contributionsController.paymentDrawerState.setOpen,
          setSelectedItems: contributionsController.paymentDrawerState.setSelectedItems
        }}
        openItemsState={{
          open: openItemsPaymentDrawerOpen,
          items: openItemsPaymentDrawerItems,
          selectedItems: openItemsPaymentDrawerSelectedItems,
          setOpen: setOpenItemsPaymentDrawerOpen,
          setSelectedItems: setOpenItemsPaymentDrawerSelectedItems
        }}
      />

      <AccessMediumAssignModal
        open={accessMediaOpen}
        saving={accessMediaSaving}
        readerScope={{
          studioName: allowedStudios.find(studio => studio.id === assignmentStudioId)?.name,
          hasStudio: !!assignmentStudioId,
          readers: assignmentReaders.readers,
          loading: assignmentReaders.isLoading,
          failed: assignmentReaders.isError || studiosLoadFailed,
          onRetry: () => { if (studiosLoadFailed) refetchStudios(); else void assignmentReaders.refetch(); }
        }}
        onScanAgain={retryAccessMediumScan}
        scanHint={accessMediumScanHint}
        scanConflict={accessMediumScanConflict}
        text={text}
        onClose={closeAccessMediaDrawer}
        onReassign={reassignAccessMedium}
      />

      {canWriteFinance && (
        <PersonContributionEditDrawer
          personId={personId}
          open={contributionsController.editOpen}
          contribution={contributionsController.edit}
          form={contributionsController.editForm}
          api={auth.api}
          message={feedback}
          modal={modal}
          text={text}
          units={units}
          decimalSeparator={decimalSeparator}
          currencyCode={currencyCode}
          moneyPlaceholder={moneyPlaceholder}
          paymentMethodSelectOptions={paymentMethodSelectOptions}
          onClose={() => {
            contributionsController.closeEdit();
          }}
        />
      )}

      {canWriteFinance && (
        <PersonContributionBulkPayDrawer
          personId={personId}
          open={contributionsController.bulkPayState.open}
          form={contributionsController.bulkPayState.form}
          contributions={contributionsController.bulkPayState.contributions}
          api={auth.api}
          message={feedback}
          text={text}
          decimalSeparator={decimalSeparator}
          currencyCode={currencyCode}
          moneyPlaceholder={moneyPlaceholder}
          units={units}
          dateInputFormat={dateInputFormat}
          paymentMethodSelectOptions={paymentMethodSelectOptions}
          onClose={() => contributionsController.bulkPayState.setOpen(false)}
          onContributionsChange={contributionsController.bulkPayState.setContributions}
          onReloadContributions={() => {
            invalidateFinanceQueries();
            void contributionsRef.current?.revalidateCurrentQuery();
          }}
        />
      )}

      {canCancelContracts && (
      <CancelContractDrawer
        open={contractCancelOpen}
        onClose={() => {
          setContractCancelOpen(false);
          setContractCancelItem(null);
          setCancelChoosablePackageIds(null);
          setMemberAppRequestIdForDrawer(null);
        }}
        contractPackage={contractCancelItem}
        choosablePackageIds={cancelChoosablePackageIds}
        personId={personId}
        person={person}
        memberAppRequestId={memberAppRequestIdForDrawer}
        requestedEffectiveDate={cancelPrefillFromRequest?.requestedEffectiveDate}
        onOpenDocumentReview={openDocumentReview}
      />
      )}

      {canWriteFinance && (
        <PersonPaymentEditDrawer
          open={paymentEditOpen}
          payment={paymentEdit}
          readOnly={!canWriteFinance || !paymentEdit || !canEditPersonPayment(paymentEdit)}
          canCancel={canCancelPayments}
          form={paymentEditForm}
          api={auth.api}
          message={feedback}
          modal={modal}
          text={text}
          units={units}
          personId={personId}
          decimalSeparator={decimalSeparator}
          currencyCode={currencyCode}
          moneyPlaceholder={moneyPlaceholder}
          dateInputFormat={dateInputFormat}
          paymentMethodNameByType={paymentMethodNameByType}
          paymentMethodSelectOptions={paymentMethodSelectOptions}
          onClose={() => {
            setPaymentEditOpen(false);
            setPaymentEdit(null);
            paymentEditForm.resetFields();
          }}
        />
      )}

      <PersonPaymentCancellationDrawer
        canWriteFinance={canCancelPayments}
        open={paymentCancellationDrawerOpen}
        payments={paymentCancellationDrawerPayments}
        personId={personId}
        onClose={() => {
          setPaymentCancellationDrawerOpen(false);
          setPaymentCancellationDrawerPayments(null);
        }}
        onSuccess={(result) => {
          setPaymentCancellationReminders(result.reminderCases.length > 0 ? result : null);
          // Rückerstattung per Überweisung: gleich an die Bank senden; Abbrechen lässt den Auftrag in den Zahlungen offen.
          if (result.transferOrderIds?.length) setRefundTransfer({ kind: "submit", transferOrderIds: result.transferOrderIds });
        }}
      />
      <RefundTransferDialogs task={refundTransfer} onClose={closeRefundTransfer} onDone={() => {
        invalidateFinanceQueries();
        paymentsRef.current?.reload();
      }} />

      <PaymentCancellationReminders
        pending={paymentCancellationReminders}
        onChange={setPaymentCancellationReminders}
        onCreated={() => {
          invalidateFinanceQueries();
          openItemsRef.current?.reload();
        }}
      />

      </div>
    </LoadingOverlay>
    </React.Activity></>
    );
  }
